import React, { useState, useRef, useMemo, Children, lazy, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useBusiness } from './BusinessContext';
import { LeadDetailModal, PIPELINE_STAGES, type Lead, type LeadStatus } from './LeadDetailModal';
import { XIcon, SparklesIcon, MessageSquareIcon, TrendingUpIcon, SettingsIcon, FileTextIcon, DollarSignIcon, BotIcon, CheckCircle2Icon, SendIcon, UserIcon, EditIcon, CheckIcon, EyeIcon, ClockIcon, TrashIcon, ArchiveIcon, ToggleRightIcon, UploadCloudIcon, PlusIcon, ImageIcon, MapPinIcon, ExternalLinkIcon, NavigationIcon, SearchIcon, CalendarIcon, LayersIcon, ChevronRightIcon } from 'lucide-react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { CatalogItem } from '../data/catalogItems';
import { AVAILABILITY_COPY, CategoryBlueprint, PRICING_FIELDS, blueprint } from '../data/productCategories';
import { OptionsEditor } from './catalog/OptionsEditor';
import { SellingRecommendations } from './catalog/SellingRecommendations';
import { catKey, itemKey, useSelling } from '../data/sellingRules';

/** Location only earns its space where a customer would actually go somewhere. */
const SHOWS_LOCATION = ['vehicle-rental', 'vehicle-sale', 'property-sale', 'property-rent', 'restaurant', 'service'];
// Fix Leaflet default icon issue
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png'
});
interface CatalogItemModalProps {
  onClose: () => void;
  /** The item being edited. Its category decides what every tab asks for. */
  item: CatalogItem;
  /** The merchant's own categories, for recommendation targets. */
  categories?: string[];
  /** Opens the category-level selling recommendations. */
  onOpenSellingRules?: (category?: string) => void;
}
type Tab = 'Overview' | 'Pricing' | 'AI Knowledge' | 'Leads' | 'Activity' | 'Settings';
export function CatalogItemModal({
  onClose,
  item,
  categories = [],
  onOpenSellingRules
}: CatalogItemModalProps) {
  const [activeTab, setActiveTab] = useState<Tab>('Overview');
  const bp = blueprint(item.kind);
  const itemTitle = item.title;
  const tabs: {
    id: Tab;
    icon: React.ReactNode;
  }[] = [{
    id: 'Overview',
    icon: <FileTextIcon className="w-4 h-4" data-id="element-1445" data-mp="2bhzfl60chhcp" />
  }, {
    id: 'Pricing',
    icon: <DollarSignIcon className="w-4 h-4" data-id="element-1446" data-mp="2ggtzycdyndyx" />
  }, {
    id: 'AI Knowledge',
    icon: <SparklesIcon className="w-4 h-4" data-id="element-1447" data-mp="2gehjk3nupbnz" />
  }, {
    id: 'Leads',
    icon: <TrendingUpIcon className="w-4 h-4" data-id="element-1448" data-mp="22hul1wg92dfy" />
  }, {
    id: 'Activity',
    icon: <TrendingUpIcon className="w-4 h-4" data-id="element-1449" data-mp="26i4sldjoxbwx" />
  }, {
    id: 'Settings',
    icon: <SettingsIcon className="w-4 h-4" data-id="element-1450" data-mp="1nvdtd839kfdw" />
  }];
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-1451" data-mp="1iviyktgdb5nk">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} data-id="element-1452" data-mp="1wv0tlqk9bf5d" />
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} className="relative w-full max-w-5xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col h-[85vh]" data-id="element-1453" data-mp="1rw7drrhhzc21">
        {/* Header */}
        <div className="flex-shrink-0 px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-white z-10" data-id="element-1454" data-mp="2bl82pfodjlte">
          <div className="flex items-center gap-4" data-id="element-1455" data-mp="2pjzgt238l7hj">
            <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0" data-id="element-1456" data-mp="2klecaqgyc1na">
              {item.image && <img src={item.image} alt="" className="w-full h-full object-cover" data-id="element-1457" data-mp="34kwpv42rvt5a" />}
            </div>
            <div className="min-w-0" data-id="element-1458" data-mp="3dh4bslxx1nwe">
              <div className="flex items-center gap-2" data-id="element-1459" data-mp="3ienoviivnumr">
                <h2 className="text-xl font-display font-bold text-dark-navy truncate" data-id="element-1460" data-mp="3ig8klbgxhjm7">
                  {itemTitle}
                </h2>
                <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${item.status === 'Active' ? 'bg-green-100 text-green-700' : item.status === 'Unavailable' ? 'bg-warning/10 text-warning' : 'bg-slate-100 text-slate-500'}`} data-id="element-1461" data-mp="2pox9751lkkoj">
                  {item.status}
                </span>
              </div>
              <div className="flex items-center gap-3 text-sm text-slate-500 mt-1 flex-wrap" data-id="element-1462" data-mp="9xsvqurvtolv">
                <span className="px-2 py-0.5 bg-slate-100 text-slate-700 text-xs font-medium rounded-md" data-id="element-1463" data-mp="evuetfpb5nxc">
                  {item.category}
                </span>
                <span data-id="element-1464" data-mp="1uftr96ft1jl1">•</span>
                <span data-id="element-1465" data-mp="1uhoe4qamzlrr">{item.priceLabel}</span>
                <span data-id="element-1466" data-mp="1ppn1325n9r0o">•</span>
                <span className="flex items-center gap-1 text-ai-purple font-medium" data-id="element-1467" data-mp="1pov5hrc1rtae">
                  <SparklesIcon className="w-3 h-3" data-id="element-1468" data-mp="1kr3h37am2pay" /> AI ready: {item.aiScore}%
                </span>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition-colors" data-id="element-1469" data-mp="1gq9oi0m84mbq">
            <XIcon className="w-5 h-5" data-id="element-1470" data-mp="16rk7ea25j5wj" />
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden" data-id="element-1471" data-mp="11s7k5o7dbwtf">
          {/* Sidebar Tabs */}
          <div className="w-48 flex-shrink-0 border-r border-slate-100 bg-slate-50/50 p-3 overflow-y-auto" data-id="element-1472" data-mp="314js2hle2qao">
            <div className="space-y-1" data-id="element-1473" data-mp="2w579i91jw5dw">
              {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${activeTab === tab.id ? 'bg-white text-primary-indigo shadow-sm border border-slate-200' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`} data-id="element-1474" data-mp="ugjh6y0akfuj">
                  <span className={activeTab === tab.id ? 'text-primary-indigo' : 'text-slate-400'} data-id="element-1475" data-mp="1d715mqe3cpa3">
                    {tab.icon}
                  </span>
                  {tab.id}
                </button>)}
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-y-auto bg-white p-6" data-id="element-1476" data-mp="3ow2627lho8pd">
            {activeTab === 'Overview' && <OverviewTab item={item} bp={bp} onOpenSelling={() => setActiveTab('AI Knowledge')} data-id="element-1477" data-mp="3syf8am111ohe" />}
            {activeTab === 'Pricing' && <PricingTab item={item} bp={bp} data-id="element-1478" data-mp="3gejerl2mfek6" />}
            {activeTab === 'AI Knowledge' && <AIRulesTab bp={bp} item={item} categories={categories} onOpenSellingRules={onOpenSellingRules} data-id="element-1479" data-mp="3vd3lzcyn9gjd" />}
            {activeTab === 'Leads' && <LeadsTab itemTitle={itemTitle} data-id="element-1480" data-mp="32ooedbhue0i2" />}
            {activeTab === 'Activity' && <ActivityTab data-id="element-1481" data-mp="2xq4ncwoe5yiu" />}
            {activeTab === 'Settings' && <SettingsTab onClose={onClose} bp={bp} item={item} data-id="element-1482" data-mp="3cehmu3motzi3" />}
          </div>
        </div>
      </motion.div>
    </div>;
}
const UAE_PLACES = [{
  id: '1',
  name: 'Al Quoz Industrial Area 3',
  city: 'Dubai',
  area: 'Al Quoz',
  lat: 25.1275,
  lng: 55.2289
}, {
  id: '2',
  name: 'Dubai Marina Mall',
  city: 'Dubai',
  area: 'Dubai Marina',
  lat: 25.0768,
  lng: 55.1404
}, {
  id: '3',
  name: 'Jumeirah Beach Residence',
  city: 'Dubai',
  area: 'JBR',
  lat: 25.0776,
  lng: 55.1328
}, {
  id: '4',
  name: 'Business Bay',
  city: 'Dubai',
  area: 'Business Bay',
  lat: 25.1846,
  lng: 55.2672
}, {
  id: '5',
  name: 'Downtown Dubai',
  city: 'Dubai',
  area: 'Downtown',
  lat: 25.1972,
  lng: 55.2744
}, {
  id: '6',
  name: 'Al Maryah Island',
  city: 'Abu Dhabi',
  area: 'Al Maryah',
  lat: 24.5019,
  lng: 54.3905
}, {
  id: '7',
  name: 'Yas Island',
  city: 'Abu Dhabi',
  area: 'Yas Island',
  lat: 24.4961,
  lng: 54.6039
}, {
  id: '8',
  name: 'Al Majaz Waterfront',
  city: 'Sharjah',
  area: 'Al Majaz',
  lat: 25.3216,
  lng: 55.3855
}];
function MapEvents({
  onLocationSelect
}: {
  onLocationSelect: (lat: number, lng: number) => void;
}) {
  useMapEvents({
    click(e) {
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    }
  });
  return null;
}
function OverviewTab({
  item,
  bp,
  onOpenSelling
}: {
  item: CatalogItem;
  bp: CategoryBlueprint;
  onOpenSelling: () => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  /** Where this item's recommendations come from, in one plain line. */
  const {
    rules
  } = useSelling();
  const selling = useMemo(() => {
    const own = rules[itemKey(item.id)];
    const inherited = item.category ? rules[catKey(item.category)] : undefined;
    const rule = own ?? inherited;
    if (!rule) return null;
    const counts = [rule.better.length > 0 && `${rule.better.length} better option${rule.better.length > 1 ? 's' : ''}`, rule.addons.length > 0 && `${rule.addons.length} addition${rule.addons.length > 1 ? 's' : ''}`].filter(Boolean).join(' · ');
    return {
      counts,
      source: own ? 'Custom for this item' : `Using your ${item.category} recommendations`
    };
  }, [rules, item.id, item.category]);
  const [images, setImages] = useState(item.images.length ? item.images : [item.image].filter(Boolean));
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Key details come from the item itself, so a fragrance never shows a
  // transmission and a burger never shows a model year.
  const [details, setDetails] = useState({
    title: item.title,
    price: item.priceLabel,
    status: item.status,
    description: item.description,
    specs: item.details.reduce<Record<string, string>>((acc, d) => {
      acc[d.label] = d.value;
      return acc;
    }, {})
  });
  const [location, setLocation] = useState({
    address: 'Al Quoz Industrial Area 3, Dubai, UAE',
    city: 'Dubai',
    area: 'Al Quoz',
    lat: 25.1275,
    lng: 55.2289
  });
  // Edit mode location state
  const [draftLocation, setDraftLocation] = useState(location);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const filteredPlaces = UAE_PLACES.filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()) || p.city.toLowerCase().includes(searchQuery.toLowerCase()) || p.area.toLowerCase().includes(searchQuery.toLowerCase()));
  const handlePlaceSelect = (place: (typeof UAE_PLACES)[0]) => {
    setDraftLocation({
      address: `${place.name}, ${place.city}, UAE`,
      city: place.city,
      area: place.area,
      lat: place.lat,
      lng: place.lng
    });
    setSearchQuery(place.name);
    setShowSuggestions(false);
  };
  const handleMapClick = (lat: number, lng: number) => {
    // Resolve the dropped pin to the nearest known UAE place so we can capture
    // a real city + area (lightweight reverse-geocode against the curated set).
    const nearest = UAE_PLACES.reduce((closest, place) => {
      const dist = (place.lat - lat) ** 2 + (place.lng - lng) ** 2;
      const closestDist = (closest.lat - lat) ** 2 + (closest.lng - lng) ** 2;
      return dist < closestDist ? place : closest;
    }, UAE_PLACES[0]);
    setDraftLocation({
      address: `Near ${nearest.area}, ${nearest.city}, UAE`,
      city: nearest.city,
      area: nearest.area,
      lat,
      lng
    });
    setSearchQuery('');
  };
  const saveLocation = () => {
    setLocation(draftLocation);
    // Optional: show a toast here
  };
  const mapQuery = location.address || `${location.area} ${location.city}`;
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const newImages = [...images, images[0]];
      setImages(newImages);
      setActiveImageIndex(newImages.length - 1);
    }
  };
  const handleDeleteImage = () => {
    if (images.length > 1) {
      const newImages = images.filter((_, idx) => idx !== activeImageIndex);
      setImages(newImages);
      setActiveImageIndex(Math.max(0, activeImageIndex - 1));
    }
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 10
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 24
      }
    }
  };
  return <div className="flex flex-col gap-8 pb-2" data-id="element-1483" data-mp="1vt6fty1hm5ul">
      {/* ===== HERO: Title + Grand Gallery ===== */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="flex flex-col gap-5" data-id="element-1484" data-mp="25pj58jq01xjj">
        {/* Title row */}
        <motion.div variants={itemVariants} className="flex flex-col sm:flex-row sm:items-end justify-between gap-4" data-id="element-1485" data-mp="2apdyuv15t18a">
          <div data-id="element-1486" data-mp="gb3a0mkke72i">
            <div className="flex items-center gap-3 mb-2.5" data-id="element-1487" data-mp="gbmp7vityhcu">
              <span className="px-2.5 py-1 bg-green-50 border border-green-200 text-green-700 text-xs font-bold uppercase tracking-wider rounded-lg shadow-sm" data-id="element-1488" data-mp="kc6iup4732u8">
                {details.status}
              </span>
              <span className="px-2.5 py-1 bg-slate-100 text-slate-600 text-xs font-medium rounded-lg" data-id="element-1489" data-mp="3h6c2j5eozbia">
                {details.specs.Category}
              </span>
              <span className="text-xs font-medium text-slate-400 flex items-center gap-1" data-id="element-1490" data-mp="3v37ofyg1kxfr">
                <ClockIcon className="w-3.5 h-3.5" data-id="element-1491" data-mp="3q4e7tx5gkqbd" /> Updated 2h ago
              </span>
            </div>
            <h3 className="text-3xl sm:text-4xl font-display font-bold text-dark-navy leading-tight" data-id="element-1492" data-mp="32ejxpncixfut">
              {details.title}
            </h3>
          </div>
          <div className="flex items-center gap-4 shrink-0" data-id="element-1493" data-mp="3c7hjq3gq5my6">
            <div className="flex items-baseline gap-1" data-id="element-1494" data-mp="37g976yk3llgs">
              <span className="text-3xl font-bold text-primary-indigo tracking-tight" data-id="element-1495" data-mp="37eog5iwzskno">
                <span className="text-lg text-primary-indigo/70 font-semibold mr-1" data-id="element-1496" data-mp="2jou4v5sbn45j">
                  AED
                </span>
                {details.price}
              </span>
              <span className="text-sm font-medium text-slate-500" data-id="element-1497" data-mp="u8894up5cphx">/day</span>
            </div>
            {!isEditing && <button onClick={() => setIsEditing(true)} className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-slate-700 bg-white border border-slate-200 shadow-sm rounded-xl hover:border-primary-indigo hover:text-primary-indigo hover:shadow transition-all group" data-id="element-1498" data-mp="z1awgl3bz2po">
                <EditIcon className="w-4 h-4 text-slate-400 group-hover:text-primary-indigo transition-colors" data-id="element-1499" data-mp="1hylu619bdo30" />
                Edit
              </button>}
          </div>
        </motion.div>

        {/* Grand gallery: large main image + thumbnail rail */}
        <motion.div variants={itemVariants} className="flex flex-col gap-3" data-id="element-1500" data-mp="190a6o83qv6es">
          <div className="aspect-[16/9] w-full max-w-xl max-h-[300px] bg-slate-50 rounded-2xl overflow-hidden relative group shadow-sm border border-slate-200/60" data-id="element-1501" data-mp="3onqo9ckjl3zk">
            <AnimatePresence mode="wait" data-id="element-1502" data-mp="1z8pq6zyzaoe1">
              <motion.img key={activeImageIndex} src={images[activeImageIndex]} alt="Main" className="w-full h-full object-cover" initial={{
              opacity: 0,
              scale: 1.02
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.2
            }} data-id="element-1503" data-mp="1z7enl3h9rmoi" />
            </AnimatePresence>
            <div className="absolute inset-0 bg-slate-900/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3 backdrop-blur-[2px]" data-id="element-1504" data-mp="16jskdwbyek26">
              <button className="px-4 py-2 bg-white text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 shadow-sm flex items-center gap-2 transition-colors" onClick={() => fileInputRef.current?.click()} data-id="element-1505" data-mp="30w8p2sp3pq4x">
                <UploadCloudIcon className="w-4 h-4" data-id="element-1506" data-mp="nn0e3h3d1fie" /> Replace
              </button>
              {images.length > 1 && <button className="p-2 bg-white text-red-600 rounded-lg hover:bg-red-50 shadow-sm transition-colors" onClick={handleDeleteImage} title="Delete Photo" data-id="element-1507" data-mp="sf1qr44fx88y">
                  <TrashIcon className="w-4 h-4" data-id="element-1508" data-mp="3vwsbh3gc5kkk" />
                </button>}
            </div>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-1 no-scrollbar" data-id="element-1509" data-mp="8pzopk8n5wn3">
            {images.map((img, idx) => <button key={idx} onClick={() => setActiveImageIndex(idx)} className={`w-24 h-20 rounded-xl flex-shrink-0 overflow-hidden border-2 transition-all ${idx === activeImageIndex ? 'border-primary-indigo shadow-sm ring-2 ring-primary-indigo/20 ring-offset-1' : 'border-transparent hover:border-slate-300 opacity-70 hover:opacity-100'}`} data-id="element-1510" data-mp="dpm2busdtewf" data-mpi={idx}>
                <img src={img} alt={`Thumb ${idx}`} className="w-full h-full object-cover" data-id="element-1511" data-mp="vtdzb82banos" />
              </button>)}
            <button className="w-24 h-20 rounded-xl flex-shrink-0 border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-500 hover:border-primary-indigo hover:text-primary-indigo hover:bg-indigo-50/50 transition-colors bg-slate-50" onClick={() => fileInputRef.current?.click()} data-id="element-1512" data-mp="14t0z5pfe7lfq">
              <PlusIcon className="w-5 h-5 mb-0.5" data-id="element-1513" data-mp="1sgiwe0hffbq3" />
              <span className="text-[10px] font-bold uppercase tracking-wider" data-id="element-1514" data-mp="1oibdp03ek0c9">
                Add
              </span>
            </button>
          </div>
          <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleImageUpload} data-id="element-1515" data-mp="3q8mxssyjy7de" />
        </motion.div>
      </motion.div>

      {/* ===== FULL-WIDTH DETAILS ===== */}
      {isEditing ? <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} className="flex flex-col" data-id="element-1516" data-mp="3k84k6j5p2h6v">
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-100" data-id="element-1517" data-mp="2wg7hbhk82zhw">
            <h3 className="text-xl font-bold text-dark-navy flex items-center gap-2" data-id="element-1518" data-mp="31gbnadni8btv">
              <EditIcon className="w-5 h-5 text-primary-indigo" data-id="element-1519" data-mp="2ng0ydg5r97bc" />
              Edit Details
            </h3>
            <div className="flex items-center gap-2" data-id="element-1520" data-mp="2seudfd0ox6dm">
              <button onClick={() => setIsEditing(false)} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 bg-slate-50 hover:bg-slate-100 rounded-xl transition-colors" data-id="element-1521" data-mp="2sfdrujr4tcmw">
                Cancel
              </button>
              <button onClick={() => setIsEditing(false)} className="px-4 py-2 bg-primary-indigo text-white text-sm font-medium rounded-xl hover:bg-indigo-700 transition-all flex items-center gap-2" data-id="element-1522" data-mp="1u6cdz9qtp8uv">
                <CheckIcon className="w-4 h-4" data-id="element-1523" data-mp="1p8kjq92phani" /> Save Changes
              </button>
            </div>
          </div>

          <div className="space-y-6" data-id="element-1524" data-mp="2cy54mxif6hzf">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-1525" data-mp="2cz7ud240aedr">
              <div className="sm:col-span-2" data-id="element-1526" data-mp="2hwgeh88psshn">
                <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-1527" data-mp="2hv5bvbr09qs4">
                  Title
                </label>
                <input type="text" value={details.title} onChange={e => setDetails({
              ...details,
              title: e.target.value
            })} className="w-full px-4 py-2.5 bg-slate-50/50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo outline-none transition-all" data-id="element-1528" data-mp="27yshscrjtawu" />
              </div>
              <div data-id="element-1529" data-mp="yxif0ernrenk">
                <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-1530" data-mp="ywqxu8yt5jgt">
                  Price (AED/day)
                </label>
                <div className="relative" data-id="element-1531" data-mp="69mpbu0ian0a">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-medium" data-id="element-1532" data-mp="6afmc39nxy58">
                    AED
                  </span>
                  <input type="text" value={details.price} onChange={e => setDetails({
                ...details,
                price: e.target.value
              })} className="w-full pl-12 pr-4 py-2.5 bg-slate-50/50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo outline-none transition-all" data-id="element-1533" data-mp="g0hcd0lktqms" />
                </div>
              </div>
              <div data-id="element-1534" data-mp="h10zm1mahxgj">
                <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-1535" data-mp="h0izuv0f513v">
                  Status
                </label>
                <select value={details.status} onChange={e => setDetails({
              ...details,
              status: e.target.value
            })} className="w-full px-4 py-2.5 bg-slate-50/50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo outline-none transition-all appearance-none" data-id="element-1536" data-mp="lzm44d8kr3as">
                  <option value="Available" data-id="element-1537" data-mp="19p6ql63xsiot">Available</option>
                  <option value="Rented" data-id="element-1538" data-mp="1eo02wv7hm3ne">Rented</option>
                  <option value="Maintenance" data-id="element-1539" data-mp="1eqvwy8g7s86k">Maintenance</option>
                </select>
              </div>
              <div className="sm:col-span-2" data-id="element-1540" data-mp="2z8oh4wnnx5wn">
                <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-1541" data-mp="2u8sa0ojo4ycv">
                  Description
                </label>
                <textarea rows={4} value={details.description} onChange={e => setDetails({
              ...details,
              description: e.target.value
            })} className="w-full px-4 py-3 bg-slate-50/50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo outline-none resize-none transition-all leading-relaxed" data-id="element-1542" data-mp="2pexvdzix79kn" />
              </div>
            </div>

            <div data-id="element-1543" data-mp="4q09epkfj7ej">
              <div className="flex items-center gap-2 mb-4" data-id="element-1544" data-mp="4ofg139upui9">
                <div className="h-px flex-1 bg-slate-100" data-id="element-1545" data-mp="3vxm967412pq4" />
                <span className="text-xs font-bold uppercase tracking-wider text-slate-400" data-id="element-1546" data-mp="7rv1f6kw4ydn">
                  Key Specifications
                </span>
                <div className="h-px flex-1 bg-slate-100" data-id="element-1547" data-mp="cqpsswwxbv9d" />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-id="element-1548" data-mp="3tytay4o343w2">
                {Object.entries(details.specs).map(([key, val]) => <div key={key} className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm" data-id="element-1549" data-mp="3l7kn3nzno7bu">
                    <label className="block text-xs font-medium text-slate-500 mb-1.5" data-id="element-1550" data-mp="3pzc69dghfary">
                      {key}
                    </label>
                    <input type="text" value={val} onChange={e => setDetails({
                ...details,
                specs: {
                  ...details.specs,
                  [key]: e.target.value
                }
              })} className="w-full px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo outline-none text-sm transition-all" data-id="element-1551" data-mp="3g7ogri3hk3t4" />
                  </div>)}
              </div>
            </div>

            {/* Interactive Product Location Editor */}
            <div data-id="element-1552" data-mp="31m0xs090r5bu">
              <div className="flex items-center gap-2 mb-4" data-id="element-1553" data-mp="31kg2u9islkdg">
                <div className="h-px flex-1 bg-slate-100" data-id="element-1554" data-mp="3fcw69bflvkew" />
                <span className="text-xs font-bold uppercase tracking-wider text-slate-400" data-id="element-1555" data-mp="3fce0no71ztub">
                  Product Location
                </span>
                <div className="h-px flex-1 bg-slate-100" data-id="element-1556" data-mp="3pak4ibcf7a1t" />
              </div>

              <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm" data-id="element-1557" data-mp="3kc0he7m3bs7v">
                <div className="p-4 border-b border-slate-100 bg-slate-50/50" data-id="element-1558" data-mp="2hyksts0yeq2i">
                  <label className="block text-sm font-semibold text-slate-700 mb-2" data-id="element-1559" data-mp="2hwht2ga01ikl">
                    Search Location
                  </label>
                  <div className="relative" data-id="element-1560" data-mp="2qz0pahja4wxv">
                    <SearchIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-1561" data-mp="2qx64r4bx76ub" />
                    <input value={searchQuery} onChange={e => {
                  setSearchQuery(e.target.value);
                  setShowSuggestions(true);
                }} onFocus={() => setShowSuggestions(true)} placeholder="Search places in UAE..." className="w-full pl-9 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo outline-none transition-all text-sm" data-id="element-1562" data-mp="2vrtxxghvcpf8" />
                    {showSuggestions && searchQuery && filteredPlaces.length > 0 && <div className="absolute z-20 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-48 overflow-y-auto" data-id="element-1563" data-mp="1802uw2h2y9an">
                          {filteredPlaces.map(place => <button key={place.id} onClick={() => handlePlaceSelect(place)} className="w-full text-left px-4 py-2.5 hover:bg-slate-50 border-b border-slate-50 last:border-0 transition-colors flex items-center gap-3" data-id="element-1564" data-mp="y36ivmrz351c">
                              <MapPinIcon className="w-4 h-4 text-slate-400 shrink-0" data-id="element-1565" data-mp="1lu2hb2qxoyhk" />
                              <div data-id="element-1566" data-mp="1hq4kwzszu1s7">
                                <div className="text-sm font-medium text-slate-900" data-id="element-1567" data-mp="acko2x6zbh9a">
                                  {place.name}
                                </div>
                                <div className="text-xs text-slate-500" data-id="element-1568" data-mp="6dbtxr811hd7">
                                  {place.area}, {place.city}
                                </div>
                              </div>
                            </button>)}
                        </div>}
                  </div>
                </div>

                <div className="h-64 relative z-10" data-id="element-1569" data-mp="21f6v3ulje5x2">
                  <MapContainer center={[draftLocation.lat, draftLocation.lng]} zoom={13} scrollWheelZoom={false} className="w-full h-full" data-id="element-1570" data-mp="2p3yfw0wsrlzt">
                    <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-1571" data-mp="2fd3wr5eul2il" />
                    <Marker position={[draftLocation.lat, draftLocation.lng]} data-id="element-1572" data-mp="380znlthqk07g" />
                    <MapEvents onLocationSelect={handleMapClick} data-id="element-1573" data-mp="3cuvhokuvp2xc" />
                  </MapContainer>
                </div>

                <div className="p-4 bg-slate-50/50 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-1574" data-mp="3m600lqfsgkxe">
                  <div data-id="element-1575" data-mp="3vyfkitanbjkx">
                    <div className="text-sm font-semibold text-slate-900" data-id="element-1576" data-mp="3vxnxw7z10w6s">
                      {draftLocation.address}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5" data-id="element-1577" data-mp="3398j9mjt4fvz">
                      Coordinates: {draftLocation.lat.toFixed(4)},{' '}
                      {draftLocation.lng.toFixed(4)}
                    </div>
                  </div>
                  <button onClick={saveLocation} className="px-4 py-2 bg-primary-indigo text-white text-sm font-semibold rounded-xl hover:bg-indigo-700 transition-colors flex items-center gap-2 shrink-0 shadow-sm" data-id="element-1578" data-mp="3d8h9c4bjxad2">
                    <CheckIcon className="w-4 h-4" data-id="element-1579" data-mp="nj5jzpmemmf9" /> Save Location
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div> : <motion.div variants={containerVariants} initial="hidden" animate="show" className="flex flex-col gap-8" data-id="element-1580" data-mp="27jolvkd0ggn1">
          {/* AI Readiness Card (full width) */}
          <motion.div variants={itemVariants} data-id="element-1581" data-mp="1ykb8whpoh9xk">
            <div className="relative overflow-hidden bg-purple-50 border border-purple-100 rounded-2xl p-5 shadow-sm" data-id="element-1582" data-mp="1ylmcohj4ihoo">
              <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-1583" data-mp="2r875znj5t3uv">
                <div className="flex items-center gap-3" data-id="element-1584" data-mp="2mgp2ostnyr6q">
                  <div className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center shrink-0" data-id="element-1585" data-mp="2mf47r23ft68c">
                    <SparklesIcon className="w-5 h-5 text-ai-purple" data-id="element-1586" data-mp="3exjaia3jknqu" />
                  </div>
                  <div data-id="element-1587" data-mp="3ezclu2p3nw56">
                    <h4 className="font-bold text-slate-900 text-sm" data-id="element-1588" data-mp="3a69nzidai0j6">
                      AI readiness
                    </h4>
                    <p className="text-xs text-slate-500 mt-0.5" data-id="element-1589" data-mp="3hj96f5n6cdp0">
                      {item.missing.length > 0 ? `Add ${Math.min(3, bp.readiness.length)} details to help Talab answer customers more accurately.` : 'Talab has everything it needs to answer customers about this item.'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3" data-id="element-1590" data-mp="4amn48gxq3yi">
                  <div className="w-40 h-2.5 bg-white rounded-full overflow-hidden shadow-inner" data-id="element-1591" data-mp="9a914k4l7o8d">
                    <motion.div initial={{
                  width: 0
                }} animate={{
                  width: `${item.aiScore}%`
                }} transition={{
                  duration: 0.9,
                  delay: 0.1,
                  ease: 'easeOut'
                }} className="h-full bg-ai-purple rounded-full" data-id="element-1592" data-mp="e1ha5f2097l0" />
                  </div>
                  <span className="font-bold text-ai-purple text-lg" data-id="element-1593" data-mp="2k3cnltnygztn">
                    {item.aiScore}%
                  </span>
                </div>
              </div>
              {item.aiScore < 90 && <div className="relative flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-purple-100" data-id="element-1594" data-mp="2t2g4cqznh34z">
                  <span className="text-xs font-semibold text-slate-500" data-id="element-1595" data-mp="37qkq63u01urz">
                    Improve by adding
                  </span>
                  {bp.readiness.slice(0, 4).map(r => <span key={r} className="px-2.5 py-1 rounded-full bg-white border border-purple-100 text-xs font-semibold text-slate-600" data-id="element-1596" data-mp="3gsagf6c5ano6">
                      {r}
                    </span>)}
                </div>}

              {/* Selling recommendations are optional — never a gap that makes
                  the listing look incomplete, just an opportunity. */}
              <button onClick={onOpenSelling} className="relative w-full flex items-center justify-between gap-3 mt-4 pt-4 border-t border-purple-100 text-left group" data-id="element-1597" data-mp="1x0dd8f8lh9e">
                <span className="min-w-0" data-id="element-1598" data-mp="3ob0jkg4d5mf1">
                  <span className="block text-xs font-bold text-slate-900" data-id="element-1599" data-mp="3o9yvnb2915fw">
                    Selling recommendations
                  </span>
                  <span className="block text-xs text-slate-500 mt-0.5" data-id="element-1600" data-mp="3fcoitl9s988v">
                    {selling ? `${selling.counts} · ${selling.source}` : 'Not set up yet — improve sales potential'}
                  </span>
                </span>
                <span className="inline-flex items-center gap-1.5 flex-shrink-0 text-xs font-bold text-ai-purple" data-id="element-1601" data-mp="zjzdxu91v9ck">
                  {selling ? 'Ready' : 'Set up'}
                  <ChevronRightIcon className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform duration-150 ease-out" data-id="element-1602" data-mp="13jrogci07pkk" />
                </span>
              </button>
            </div>
          </motion.div>

          {/* Description (full width) */}
          <motion.div variants={itemVariants} data-id="element-1603" data-mp="4vz6cpvegxc5">
            <h4 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2" data-id="element-1604" data-mp="3w3lg908ppw1g">
              <FileTextIcon className="w-4 h-4 text-slate-400" data-id="element-1605" data-mp="jta057v5gs" /> Description
            </h4>
            <div className="bg-slate-50/50 border border-slate-100 rounded-2xl p-5" data-id="element-1606" data-mp="3s42w46optozo">
              <p className="text-sm text-slate-600 leading-relaxed max-w-3xl" data-id="element-1607" data-mp="3n9yp5nfg9gyl">
                {details.description}
              </p>
            </div>
          </motion.div>

          {/* Key Specs Grid (full width) */}
          <motion.div variants={itemVariants} data-id="element-1608" data-mp="3db94jmw65ien">
            <h4 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2" data-id="element-1609" data-mp="38cxv3d9sd9wy">
              <SettingsIcon className="w-4 h-4 text-slate-400" data-id="element-1610" data-mp="34c5n0ncnhb29" /> Key details
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-id="element-1611" data-mp="1jv4rsr4ancth">
              {Object.entries(details.specs).map(([key, val]) => <div key={key} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:shadow-md hover:border-slate-300 transition-all group" data-id="element-1612" data-mp="1ny1easwyft2p">
                  <span className="block text-xs font-medium text-slate-500 mb-1 group-hover:text-primary-indigo transition-colors" data-id="element-1613" data-mp="106dvyzdrdqc1">
                    {key}
                  </span>
                  <span className="block font-semibold text-slate-900 text-sm truncate" title={val} data-id="element-1614" data-mp="153dy4z7hm8xv">
                    {val}
                  </span>
                </div>)}
            </div>
          </motion.div>

          {/* Availability, in the language this trade uses */}
          <motion.div variants={itemVariants} data-id="element-1615" data-mp="mdxmpgu9kw2o">
            <h4 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2" data-id="element-1616" data-mp="mcmjpj8n7scm">
              <CalendarIcon className="w-4 h-4 text-slate-400" data-id="element-1617" data-mp="ec6t178kvpr6" />
              {AVAILABILITY_COPY[bp.availability].label}
            </h4>
            <div className="bg-slate-50/50 border border-slate-100 rounded-2xl p-5 flex items-center justify-between gap-4" data-id="element-1618" data-mp="o4mbe5nsegcl">
              <p className="text-sm font-bold text-slate-900" data-id="element-1619" data-mp="j61cqao6o4q9">
                {item.status === 'Unavailable' ? AVAILABILITY_COPY[bp.availability].none : AVAILABILITY_COPY[bp.availability].counts ? AVAILABILITY_COPY[bp.availability].plenty(item.stock ?? 0) : AVAILABILITY_COPY[bp.availability].plenty(0)}
              </p>
              <button className="text-xs font-bold text-primary-indigo hover:text-indigo-700 transition-colors duration-150 ease-out" data-id="element-1620" data-mp="dgz20h22671">
                Update
              </button>
            </div>
          </motion.div>

          {/* Options — never called variants in front of a merchant */}
          <motion.div variants={itemVariants} data-id="element-1621" data-mp="2tq8ut97pmspi">
            <OptionsEditor item={item} bp={bp} data-id="element-1622" data-mp="2ttn0aqbo7i16" />
          </motion.div>

          {/* Location, only where it means something to a customer */}
          <motion.div variants={itemVariants} className={SHOWS_LOCATION.includes(bp.id) ? '' : 'hidden'} data-id="element-1623" data-mp="2krdn1sp0pak3">
            <h4 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2" data-id="element-1624" data-mp="2prt16mh4im34">
              <MapPinIcon className="w-4 h-4 text-slate-400" data-id="element-1625" data-mp="3dg2t864xk62p" /> Location
            </h4>
            <div className="bg-slate-50/50 border border-slate-100 rounded-2xl p-5 flex flex-col gap-4" data-id="element-1626" data-mp="3hgczzl0jr0im">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-id="element-1627" data-mp="33p7va4ibni1f">
                <div className="flex items-start gap-3" data-id="element-1628" data-mp="38mzvdlt4eagp">
                  <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 shadow-sm flex items-center justify-center shrink-0" data-id="element-1629" data-mp="87t1kb3q1ll">
                    <MapPinIcon className="w-5 h-5 text-primary-indigo" data-id="element-1630" data-mp="50skwgag64mh" />
                  </div>
                  <div data-id="element-1631" data-mp="214751r9zl65w">
                    <p className="text-sm font-semibold text-slate-900" data-id="element-1632" data-mp="214fhxj640bh3">
                      {location.area}, {location.city}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5 max-w-md" data-id="element-1633" data-mp="1rduhdq6vkwvj">
                      {location.address}
                    </p>
                    <div className="flex items-center gap-2 mt-2" data-id="element-1634" data-mp="1mbn5f0m1pgt7">
                      <span className="px-2.5 py-1 bg-white border border-slate-200 text-slate-600 text-xs font-medium rounded-lg" data-id="element-1635" data-mp="2k05geweu2pq8">
                        City: {location.city}
                      </span>
                      <span className="px-2.5 py-1 bg-white border border-slate-200 text-slate-600 text-xs font-medium rounded-lg" data-id="element-1636" data-mp="2a1ps1176xukf">
                        Area: {location.area}
                      </span>
                    </div>
                  </div>
                </div>
                <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapQuery)}`} target="_blank" rel="noreferrer" className="px-4 py-2.5 bg-white border border-slate-200 text-slate-700 text-sm font-medium rounded-xl hover:border-primary-indigo hover:text-primary-indigo transition-colors flex items-center justify-center gap-2 whitespace-nowrap shrink-0" data-id="element-1637" data-mp="ulh7541jhnwf">
                  <ExternalLinkIcon className="w-4 h-4" data-id="element-1638" data-mp="3p0n8dwq7e914" /> Open in Maps
                </a>
              </div>
              <div className="rounded-xl overflow-hidden border border-slate-200 shadow-sm h-56 relative z-10" data-id="element-1639" data-mp="3tyyi87gi0jjc">
                <MapContainer center={[location.lat, location.lng]} zoom={14} scrollWheelZoom={false} className="w-full h-full" data-id="element-1640" data-mp="1wqngcxh9txd">
                  <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-1641" data-mp="23oljjk793puh" />
                  <Marker position={[location.lat, location.lng]} data-id="element-1642" data-mp="2kedl6nhssvog" />
                </MapContainer>
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </div>;
}
interface DeliveryRow {
  id: number;
  location: string;
  charge: string;
}
interface AddonRow {
  id: number;
  item: string;
  type: string;
  amount: string;
}
function PricingTab({
  item,
  bp
}: {
  item: CatalogItem;
  bp: CategoryBlueprint;
}) {
  // Rentals genuinely need day/week/month rates, deposits and delivery. Every
  // other trade gets the lean model that fits it instead.
  if (bp.pricing !== 'rental') return <AdaptivePricing item={item} bp={bp} data-id="element-1643" data-mp="2ga5yswytbrte" />;
  return <RentalPricing data-id="element-1644" data-mp="2gbz345lydzy8" />;
}

/**
 * One lead price, then optional sections the merchant opens only if they need
 * them — plus per-option pricing where the item has options.
 */
function AdaptivePricing({
  item,
  bp
}: {
  item: CatalogItem;
  bp: CategoryBlueprint;
}) {
  const model = PRICING_FIELDS[bp.pricing];
  const [lead, setLead] = useState(item.priceLabel.replace(/[^0-9.]/g, ''));
  const [opened, setOpened] = useState<string[]>([]);
  const [extra, setExtra] = useState<Record<string, string>>({});
  return <div className="max-w-3xl space-y-8" data-id="element-1645" data-mp="3s5t3k88niey2">
      <div data-id="element-1646" data-mp="3n552suumejqn">
        <h3 className="text-xl font-bold text-dark-navy flex items-center gap-2" data-id="element-1647" data-mp="3n3k731wkkur7">
          <DollarSignIcon className="w-5 h-5 text-primary-indigo" data-id="element-1648" data-mp="3i628o5g6hxxg" /> Pricing
        </h3>
        <p className="text-sm text-slate-500 mt-1" data-id="element-1649" data-mp="3df29suni15sb">
          What Talab quotes customers for this {bp.itemNoun}.
        </p>
      </div>

      <section data-id="element-1650" data-mp="39e09eahuinor">
        <label className="block text-sm font-semibold text-dark-navy mb-2" data-id="element-1651" data-mp="39dgr2soa9xa7">
          {model.lead}
        </label>
        <div className="relative max-w-xs" data-id="element-1652" data-mp="34c17l51r7ytx">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-400" data-id="element-1653" data-mp="2zfapkc6e2t5v">
            AED
          </span>
          <input value={lead} onChange={e => setLead(e.target.value)} className="w-full pl-14 pr-4 py-3 bg-white border border-slate-200 rounded-xl text-lg font-bold text-dark-navy outline-none focus:border-primary-indigo/50 focus:ring-4 focus:ring-primary-indigo/10 transition-[border-color,box-shadow] duration-150 ease-out" data-id="element-1654" data-mp="2ugpzh5kuppv5" />
        </div>
        {model.leadHint && <p className="text-xs text-slate-400 mt-2" data-id="element-1655" data-mp="2fv2czdr6e994">{model.leadHint}</p>}
      </section>

      {/* Prices by option, when the item has them */}
      {item.options.length > 0 && <section data-id="element-1656" data-mp="38k124utpubnx">
          <h4 className="text-sm font-bold text-slate-900 mb-3" data-id="element-1657" data-mp="2uqi4pc4a9t2c">
            Prices by option
          </h4>
          <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100" data-id="element-1658" data-mp="2yrv5h7sir62e">
            {item.options.flatMap(g => g.values.map(v => <div key={`${g.name}-${v.label}`} className="flex items-center justify-between gap-4 px-5 py-3.5" data-id="element-1659" data-mp="3r8eaz8097xnd">
                  <div className="min-w-0" data-id="element-1660" data-mp="1frm82v4p7ep4">
                    <p className="text-sm font-semibold text-dark-navy" data-id="element-1661" data-mp="1aulzacggrxub">
                      {v.label}
                    </p>
                    <p className="text-[11px] text-slate-400" data-id="element-1662" data-mp="1pi74cbwwg74v">{g.name}</p>
                  </div>
                  <p className="text-sm font-bold text-dark-navy" data-id="element-1663" data-mp="1kl6yo23yp6ea">
                    {v.price ?? `AED ${lead}`}
                  </p>
                </div>))}
          </div>
        </section>}

      {/* Everything else, only if asked for */}
      <section data-id="element-1664" data-mp="16ta8i3lut6kj">
        <div className="flex flex-wrap gap-2" data-id="element-1665" data-mp="16t0qb271w3pe">
          {model.optional.filter(o => !opened.includes(o)).map(o => <button key={o} onClick={() => setOpened(s => [...s, o])} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-dashed border-slate-300 text-xs font-bold text-slate-600 hover:border-primary-indigo/50 hover:text-primary-indigo transition-colors duration-150 ease-out" data-id="element-1666" data-mp="11w0mkxxobd1g">
                <PlusIcon className="w-3.5 h-3.5" data-id="element-1667" data-mp="rrc1k6r11b3c" />
                Add {o.toLowerCase()}
              </button>)}
        </div>
        {opened.length > 0 && <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4" data-id="element-1668" data-mp="10rqotbnjx09h">
            {opened.map(o => <div key={o} data-id="element-1669" data-mp="15pil0hv8b8jh">
                <label className="block text-xs font-semibold text-slate-500 mb-1.5" data-id="element-1670" data-mp="15qbies8asloy">
                  {o}
                </label>
                <div className="relative" data-id="element-1671" data-mp="1fgmwv8plyda3">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-400" data-id="element-1672" data-mp="1fho6r9uw0tq8">
                    AED
                  </span>
                  <input value={extra[o] ?? ''} onChange={e => setExtra(s => ({
              ...s,
              [o]: e.target.value
            }))} placeholder="0" className="w-full pl-14 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-800 placeholder:text-slate-300 outline-none focus:border-primary-indigo/50 focus:ring-4 focus:ring-primary-indigo/10 transition-[border-color,box-shadow] duration-150 ease-out" data-id="element-1673" data-mp="1ohl37xniqwvu" />
                </div>
              </div>)}
          </div>}
      </section>
    </div>;
}
function RentalPricing() {
  const [isEditing, setIsEditing] = useState(false);
  const [base, setBase] = useState({
    daily: '350',
    weekly: '2100',
    monthly: '7500'
  });
  const [delivery, setDelivery] = useState<DeliveryRow[]>([{
    id: 1,
    location: 'Inside Dubai',
    charge: '50'
  }, {
    id: 2,
    location: 'Sharjah',
    charge: '100'
  }, {
    id: 3,
    location: 'Airport Delivery',
    charge: '75'
  }]);
  const [addons, setAddons] = useState<AddonRow[]>([{
    id: 1,
    item: 'Security Deposit',
    type: 'Refundable',
    amount: '1000'
  }, {
    id: 2,
    item: 'Additional Driver',
    type: 'Per Day',
    amount: '25'
  }, {
    id: 3,
    item: 'Child Seat',
    type: 'Fixed',
    amount: '50'
  }]);
  const num = (v: string) => parseFloat(v.replace(/,/g, '')) || 0;
  const fmt = (v: number) => v.toLocaleString('en-US');
  // Auto-computed savings vs. paying the daily rate for the whole period.
  const weeklyDiscount = Math.max(0, Math.round((1 - num(base.weekly) / (num(base.daily) * 7)) * 100));
  const monthlyDiscount = Math.max(0, Math.round((1 - num(base.monthly) / (num(base.daily) * 30)) * 100));
  const updateDelivery = (id: number, field: keyof DeliveryRow, val: string) => setDelivery(prev => prev.map(r => r.id === id ? {
    ...r,
    [field]: val
  } : r));
  const addDelivery = () => setDelivery(prev => [...prev, {
    id: Date.now(),
    location: 'New location',
    charge: '0'
  }]);
  const removeDelivery = (id: number) => setDelivery(prev => prev.filter(r => r.id !== id));
  const updateAddon = (id: number, field: keyof AddonRow, val: string) => setAddons(prev => prev.map(r => r.id === id ? {
    ...r,
    [field]: val
  } : r));
  const addAddon = () => setAddons(prev => [...prev, {
    id: Date.now(),
    item: 'New add-on',
    type: 'Fixed',
    amount: '0'
  }]);
  const removeAddon = (id: number) => setAddons(prev => prev.filter(r => r.id !== id));
  const baseCards = [{
    key: 'daily' as const,
    label: 'Daily Rate',
    discount: 0
  }, {
    key: 'weekly' as const,
    label: 'Weekly Rate',
    discount: weeklyDiscount
  }, {
    key: 'monthly' as const,
    label: 'Monthly Rate',
    discount: monthlyDiscount
  }];
  return <div className="max-w-3xl space-y-8" data-id="element-1674" data-mp="26rhw18ed5dul">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-1675" data-mp="26s9nq84uarfl">
        <div data-id="element-1676" data-mp="2bqufrk9xu4sy">
          <h3 className="text-xl font-bold text-dark-navy flex items-center gap-2" data-id="element-1677" data-mp="2bp9j9p42cbsg">
            <DollarSignIcon className="w-5 h-5 text-primary-indigo" data-id="element-1678" data-mp="1e3m0w84fztzx" /> Pricing
          </h3>
          <p className="text-sm text-slate-500 mt-1" data-id="element-1679" data-mp="1j1ds0zx9jc32">
            Set the rates, charges and add-ons the AI quotes to customers.
          </p>
        </div>
        {isEditing ? <div className="flex items-center gap-2" data-id="element-1680" data-mp="1szthyzkk0faz">
            <button onClick={() => setIsEditing(false)} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 bg-slate-50 hover:bg-slate-100 rounded-xl transition-colors" data-id="element-1681" data-mp="1t1o24bo043e0">
              Cancel
            </button>
            <button onClick={() => setIsEditing(false)} className="px-4 py-2 bg-primary-indigo text-white text-sm font-medium rounded-xl hover:bg-indigo-700 transition-colors flex items-center gap-2" data-id="element-1682" data-mp="sqv6sqrikcj9">
              <CheckIcon className="w-4 h-4" data-id="element-1683" data-mp="j00lbom3dgyv" /> Save Changes
            </button>
          </div> : <button onClick={() => setIsEditing(true)} className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-slate-700 bg-white border border-slate-200 shadow-sm rounded-xl hover:border-primary-indigo hover:text-primary-indigo hover:shadow transition-all group" data-id="element-1684" data-mp="e15ymbl0789h">
            <EditIcon className="w-4 h-4 text-slate-400 group-hover:text-primary-indigo transition-colors" data-id="element-1685" data-mp="3r9sbor0nglrj" />
            Edit Pricing
          </button>}
      </div>

      {/* Base Pricing */}
      <section data-id="element-1686" data-mp="1pif9i9q2grz6">
        <h4 className="text-sm font-bold text-slate-900 mb-3" data-id="element-1687" data-mp="1phw23my1llze">Base Pricing</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1688" data-mp="2frtacf9uwtgt">
          {baseCards.map(card => <div key={card.key} className="p-5 bg-white rounded-2xl border border-slate-200 shadow-sm" data-id="element-1689" data-mp="2athyjz0048vy">
              <div className="flex items-center justify-between mb-2" data-id="element-1690" data-mp="2phctzn5e8rcq">
                <div className="text-sm text-slate-500" data-id="element-1691" data-mp="2kjugdjgflnye">{card.label}</div>
                {card.discount > 0 && <span className="text-xs font-bold text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-md" data-id="element-1692" data-mp="1wu05v8jl4bhb">
                    Save {card.discount}%
                  </span>}
              </div>
              {isEditing ? <div className="relative" data-id="element-1693" data-mp="21rs8awhuvfzr">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-medium" data-id="element-1694" data-mp="21qqlxvve372q">
                    AED
                  </span>
                  <input type="text" value={base[card.key]} onChange={e => setBase({
              ...base,
              [card.key]: e.target.value
            })} className="w-full pl-12 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-lg font-bold text-dark-navy focus:bg-white focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo outline-none transition-all" data-id="element-1695" data-mp="3d805eieipz41" />
                </div> : <div className="text-xl font-bold text-dark-navy" data-id="element-1696" data-mp="ca4rdo3yaygp">
                  AED {fmt(num(base[card.key]))}
                </div>}
            </div>)}
        </div>
        {isEditing && <p className="text-xs text-slate-400 mt-2" data-id="element-1697" data-mp="2js8s1fp5fd7">
            Weekly and monthly savings are calculated automatically against the
            daily rate.
          </p>}
      </section>

      {/* Delivery & Pickup */}
      <section data-id="element-1698" data-mp="v3ighckzgaoe">
        <div className="flex items-center justify-between mb-3" data-id="element-1699" data-mp="14xe8iisdt1f0">
          <h4 className="text-sm font-bold text-slate-900" data-id="element-1700" data-mp="14wuzxsomfpdn">
            Delivery &amp; Pickup Charges
          </h4>
          {isEditing && <button onClick={addDelivery} className="text-xs font-semibold text-primary-indigo hover:text-indigo-700 flex items-center gap-1 transition-colors" data-id="element-1701" data-mp="vwgdgpzx848k">
              <PlusIcon className="w-3.5 h-3.5" data-id="element-1702" data-mp="lzbror9zmasa" /> Add location
            </button>}
        </div>
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-id="element-1703" data-mp="h7tmfr0xlo1i">
          <table className="w-full text-left text-sm" data-id="element-1704" data-mp="c8h1jbtmer1k">
            <thead className="bg-slate-50 border-b border-slate-200" data-id="element-1705" data-mp="c6w7rof4rc4r">
              <tr data-id="element-1706" data-mp="88opuq8xk4rz">
                <th className="px-4 py-3 font-medium text-slate-600" data-id="element-1707" data-mp="898a4dm1z596">
                  Location / Service
                </th>
                <th className="px-4 py-3 font-medium text-slate-600" data-id="element-1708" data-mp="3uelmrffgmfqk">Charge</th>
                {isEditing && <th className="px-4 py-3 w-10" data-id="element-1709" data-mp="3ugy4psl7wq3m" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-1710" data-mp="29ynj7tc92nyr">
              {delivery.map(row => <tr key={row.id} data-id="element-1711" data-mp="2exqq7jbsj49d">
                  <td className="px-4 py-2.5 text-dark-navy" data-id="element-1712" data-mp="2ju7f4fqtdmni">
                    {isEditing ? <input type="text" value={row.location} onChange={e => updateDelivery(row.id, 'location', e.target.value)} className="w-full px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:bg-white focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo outline-none transition-all" data-id="element-1713" data-mp="2nwub8suiptqi" /> : row.location}
                  </td>
                  <td className="px-4 py-2.5 font-medium" data-id="element-1714" data-mp="1cgdr4m80qoxb">
                    {isEditing ? <div className="relative w-32" data-id="element-1715" data-mp="tc0qi2b9st3q">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs" data-id="element-1716" data-mp="tag08ovznwbo">
                          AED
                        </span>
                        <input type="text" value={row.charge} onChange={e => updateDelivery(row.id, 'charge', e.target.value)} className="w-full pl-10 pr-2 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium focus:bg-white focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo outline-none transition-all" data-id="element-1717" data-mp="13357t8of4q1q" />
                      </div> : `AED ${fmt(num(row.charge))}`}
                  </td>
                  {isEditing && <td className="px-4 py-2.5 text-right" data-id="element-1718" data-mp="3ivcbdonmtkj9">
                      <button onClick={() => removeDelivery(row.id)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors" title="Remove" data-id="element-1719" data-mp="1yb77ahqv82dh">
                        <TrashIcon className="w-4 h-4" data-id="element-1720" data-mp="1tkh0r16pdth7" />
                      </button>
                    </td>}
                </tr>)}
            </tbody>
          </table>
        </div>
      </section>

      {/* Add-ons & Deposits */}
      <section data-id="element-1721" data-mp="1jnvs85vh608m">
        <div className="flex items-center justify-between mb-3" data-id="element-1722" data-mp="1jk82lht5hxz6">
          <h4 className="text-sm font-bold text-slate-900" data-id="element-1723" data-mp="15ocn7lxyn50j">
            Add-ons &amp; Deposits
          </h4>
          {isEditing && <button onClick={addAddon} className="text-xs font-semibold text-primary-indigo hover:text-indigo-700 flex items-center gap-1 transition-colors" data-id="element-1724" data-mp="300t21b4tnrh0">
              <PlusIcon className="w-3.5 h-3.5" data-id="element-1725" data-mp="33iqiaov8wmt4" /> Add item
            </button>}
        </div>
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-id="element-1726" data-mp="2yjne16n3akm7">
          <table className="w-full text-left text-sm" data-id="element-1727" data-mp="2ym02m2ncrt88">
            <thead className="bg-slate-50 border-b border-slate-200" data-id="element-1728" data-mp="2tlc28qd8i01c">
              <tr data-id="element-1729" data-mp="2olzfs6q9yuza">
                <th className="px-4 py-3 font-medium text-slate-600" data-id="element-1730" data-mp="2omiz9rvkprff">Item</th>
                <th className="px-4 py-3 font-medium text-slate-600" data-id="element-1731" data-mp="3m9rej54ze48z">Type</th>
                <th className="px-4 py-3 font-medium text-slate-600" data-id="element-1732" data-mp="3ha52gz0w2u1r">Amount</th>
                {isEditing && <th className="px-4 py-3 w-10" data-id="element-1733" data-mp="3hchl7eeh18fv" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-1734" data-mp="3chbxca1idjp5">
              {addons.map(row => <tr key={row.id} data-id="element-1735" data-mp="37i92tjj5mvvf">
                  <td className="px-4 py-2.5 text-dark-navy" data-id="element-1736" data-mp="8uv4o3418azs">
                    {isEditing ? <input type="text" value={row.item} onChange={e => updateAddon(row.id, 'item', e.target.value)} className="w-full px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:bg-white focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo outline-none transition-all" data-id="element-1737" data-mp="433cxp953z82" /> : row.item}
                  </td>
                  <td className="px-4 py-2.5 text-slate-500" data-id="element-1738" data-mp="zcgr3t6km3pp">
                    {isEditing ? <select value={row.type} onChange={e => updateAddon(row.id, 'type', e.target.value)} className="w-full px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:bg-white focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo outline-none transition-all appearance-none" data-id="element-1739" data-mp="143p1arfq5t3x">
                        <option value="Refundable" data-id="element-1740" data-mp="24dh8repb1kcx">Refundable</option>
                        <option value="Per Day" data-id="element-1741" data-mp="1zc1lrh3kh3rw">Per Day</option>
                        <option value="Fixed" data-id="element-1742" data-mp="2dcuim8hccay6">Fixed</option>
                      </select> : row.type}
                  </td>
                  <td className="px-4 py-2.5 font-medium" data-id="element-1743" data-mp="1kndrrf9dd242">
                    {isEditing ? <div className="relative w-32" data-id="element-1744" data-mp="1gmbu32v3oy47">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs" data-id="element-1745" data-mp="1gnwp0tlbuj2l">
                          AED
                        </span>
                        <input type="text" value={row.amount} onChange={e => updateAddon(row.id, 'amount', e.target.value)} className="w-full pl-10 pr-2 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium focus:bg-white focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo outline-none transition-all" data-id="element-1746" data-mp="1plp4bxr1tqpz" />
                      </div> : `AED ${fmt(num(row.amount))}`}
                  </td>
                  {isEditing && <td className="px-4 py-2.5 text-right" data-id="element-1747" data-mp="3saqek8xg6qkh">
                      <button onClick={() => removeAddon(row.id)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors" title="Remove" data-id="element-1748" data-mp="5cuoz5ssz2s">
                        <TrashIcon className="w-4 h-4" data-id="element-1749" data-mp="3nc5fictxmcxm" />
                      </button>
                    </td>}
                </tr>)}
            </tbody>
          </table>
        </div>
      </section>
    </div>;
}
// Status pill colors, aligned with the leads pipeline used in the Leads section.
function leadStatusBadge(status: LeadStatus) {
  switch (status) {
    case 'Won':
    case 'Ready To Buy':
      return 'bg-green-100 text-green-700';
    case 'Qualified':
      return 'bg-indigo-100 text-primary-indigo';
    case 'Negotiating':
      return 'bg-blue-100 text-blue-700';
    case 'Interested':
      return 'bg-amber-100 text-amber-700';
    case 'Lost':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-slate-100 text-slate-600';
  }
}
function LeadsTab({
  itemTitle
}: {
  itemTitle: string;
}) {
  // Leads MAPPED to this specific item (matchedItem === itemTitle), using the
  // same Lead shape + pipeline stages as the main Leads section so they stay
  // consistent and open the same detail view.
  const [leads, setLeads] = useState<Lead[]>([{
    name: 'Ahmed Al Mansoori',
    intentScore: 94,
    lookingFor: 'Toyota Prado',
    budget: 'AED 90,000',
    timeline: 'Within 7 Days',
    location: 'Dubai',
    matchedItem: itemTitle,
    status: 'Qualified',
    summary: 'Customer strongly prefers GCC vehicles. Ready to purchase this week.',
    phone: '+971 50 123 4567',
    email: 'ahmed@email.com',
    preferredContact: 'WhatsApp',
    preferredLanguage: 'Arabic',
    source: 'Talab AI',
    lastActive: '12 minutes ago',
    receivedDate: 'Jun 6, 2026',
    owner: 'Sara',
    nextAction: 'Call customer',
    nextActionDue: 'Today',
    consent: 'shared'
  }, {
    name: 'Omar Haddad',
    intentScore: 67,
    lookingFor: 'Family SUV',
    budget: 'AED 70,000 - 85,000',
    timeline: 'This Month',
    location: 'Sharjah',
    matchedItem: itemTitle,
    status: 'Interested',
    summary: 'Exploring options for a family vehicle. Requested availability confirmation.',
    source: 'Talab AI',
    lastActive: '3 hours ago',
    receivedDate: 'Jun 5, 2026',
    owner: 'Unassigned',
    nextAction: 'Follow up tomorrow',
    nextActionDue: 'Tomorrow',
    consent: 'requested'
  }, {
    name: 'Layla Hassan',
    intentScore: 88,
    lookingFor: 'Toyota Prado GXR',
    budget: 'AED 95,000',
    timeline: 'Within 2 Weeks',
    location: 'Dubai',
    matchedItem: itemTitle,
    status: 'Negotiating',
    summary: 'Asked about delivery to Dubai Marina and the security deposit. Strong engagement.',
    phone: '+971 52 444 1212',
    email: 'layla.h@email.com',
    preferredContact: 'Phone Call',
    preferredLanguage: 'English',
    source: 'Talab AI',
    lastActive: '1 hour ago',
    receivedDate: 'Jun 6, 2026',
    owner: 'Fadi',
    nextAction: 'Send quotation',
    nextActionDue: 'Today',
    consent: 'shared'
  }, {
    name: 'Anonymous Buyer',
    intentScore: 41,
    lookingFor: 'Compact SUV',
    budget: 'AED 60,000',
    timeline: 'Undecided',
    location: 'Dubai',
    matchedItem: itemTitle,
    status: 'Discovered',
    summary: 'Early-stage research. Asked a single pricing question.',
    source: 'Talab AI',
    lastActive: '2 days ago',
    receivedDate: 'Jun 4, 2026',
    owner: 'Unassigned',
    consent: 'not_shared'
  }]);
  const [statusFilter, setStatusFilter] = useState<'All' | LeadStatus>('All');
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const filtered = statusFilter === 'All' ? leads : leads.filter(l => l.status === statusFilter);
  const updateStatus = (status: LeadStatus) => setSelectedLead(cur => {
    if (!cur) return cur;
    setLeads(prev => prev.map(l => l.name === cur.name ? {
      ...l,
      status
    } : l));
    return {
      ...cur,
      status
    };
  });
  const updateOwner = (owner: string) => setSelectedLead(cur => {
    if (!cur) return cur;
    setLeads(prev => prev.map(l => l.name === cur.name ? {
      ...l,
      owner
    } : l));
    return {
      ...cur,
      owner
    };
  });
  return <div data-id="element-1750" data-mp="7fbh4y3w85j2">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5" data-id="element-1751" data-mp="7f35tanf589z">
        <div data-id="element-1752" data-mp="v4npjvrec9kb">
          <h3 className="text-lg font-bold text-dark-navy" data-id="element-1753" data-mp="v4xh3ns3aos4">Leads</h3>
          <p className="text-sm text-slate-500 mt-0.5" data-id="element-1754" data-mp="1049znwbxh9ow">
            {leads.length} buyers matched to this item.
          </p>
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as 'All' | LeadStatus)} className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-indigo/20" data-id="element-1755" data-mp="m3ftzlg9lmtx">
          <option value="All" data-id="element-1756" data-mp="3buku9rn25h5j">All Stages</option>
          {PIPELINE_STAGES.map(s => <option key={s} value={s} data-id="element-1757" data-mp="33bhg81o4a8d1">
              {s}
            </option>)}
        </select>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-id="element-1758" data-mp="2zaxjgz7gh6rf">
        <table className="w-full text-left text-sm" data-id="element-1759" data-mp="3d9niwlhqziiy">
          <thead className="bg-slate-50 border-b border-slate-200" data-id="element-1760" data-mp="38c54ifkyob3k">
            <tr data-id="element-1761" data-mp="389sq2ceewiv9">
              <th className="px-4 py-3 font-medium text-slate-600" data-id="element-1762" data-mp="2kmat82gaiulf">Customer</th>
              <th className="px-4 py-3 font-medium text-slate-600" data-id="element-1763" data-mp="2kk855nyp7bjc">Intent</th>
              <th className="px-4 py-3 font-medium text-slate-600" data-id="element-1764" data-mp="2fosu13s2rtqm">Stage</th>
              <th className="px-4 py-3 font-medium text-slate-600" data-id="element-1765" data-mp="2ucdz338ig316">
                Looking for
              </th>
              <th className="px-4 py-3 font-medium text-slate-600" data-id="element-1766" data-mp="2pkm4mhm8hd5r">
                Last active
              </th>
              <th className="px-4 py-3 font-medium text-slate-600 text-right" data-id="element-1767" data-mp="21tyt7md405eg">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-1768" data-mp="u3pzvl89fot">
            {filtered.map((lead, i) => <tr key={i} className="hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => setSelectedLead(lead)} data-id="element-1769" data-mp="5s54023sqotf" data-mpi={i}>
                <td className="px-4 py-3 font-medium text-dark-navy" data-id="element-1770" data-mp="ekqkfbjygjf3">
                  <div className="flex items-center gap-2" data-id="element-1771" data-mp="emtb7xsxmgkv">
                    <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-600" data-id="element-1772" data-mp="3e1f54ru40fno">
                      {lead.name.charAt(0)}
                    </div>
                    {lead.name}
                  </div>
                </td>
                <td className="px-4 py-3" data-id="element-1773" data-mp="3s051g593ubaz">
                  <span className={`font-bold ${lead.intentScore >= 80 ? 'text-green-600' : lead.intentScore >= 50 ? 'text-amber-600' : 'text-slate-500'}`} data-id="element-1774" data-mp="3rxsax3pa6smb">
                    {lead.intentScore}
                  </span>
                </td>
                <td className="px-4 py-3" data-id="element-1775" data-mp="1hd2w77f6joem">
                  <span className={`px-2 py-1 text-xs font-bold rounded-full ${leadStatusBadge(lead.status)}`} data-id="element-1776" data-mp="1cehxjcfktcsa">
                    {lead.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-slate-600 truncate max-w-[160px]" data-id="element-1777" data-mp="13mzcan3rw6sc">
                  {lead.lookingFor}
                </td>
                <td className="px-4 py-3 text-slate-500" data-id="element-1778" data-mp="yoebalgp5j2u">
                  {lead.lastActive ?? '—'}
                </td>
                <td className="px-4 py-3 text-right" data-id="element-1779" data-mp="oox9wg8h9rco">
                  <button onClick={e => {
                e.stopPropagation();
                setSelectedLead(lead);
              }} className="text-primary-indigo font-medium hover:underline" data-id="element-1780" data-mp="opovqzc9wapr">
                    View Lead
                  </button>
                </td>
              </tr>)}
            {filtered.length === 0 && <tr data-id="element-1781" data-mp="2yc7fx1r2m4ms">
                <td colSpan={6} className="px-4 py-12 text-center text-sm text-slate-400" data-id="element-1782" data-mp="32bom5xebn5t0">
                  No leads in this stage yet.
                </td>
              </tr>}
          </tbody>
        </table>
      </div>

      {selectedLead && <LeadDetailModal lead={selectedLead} onClose={() => setSelectedLead(null)} onStatusChange={updateStatus} onOwnerChange={updateOwner} data-id="element-1783" data-mp="2emdh43pxup92" />}
    </div>;
}
function ActivityTab() {
  return <div className="max-w-2xl" data-id="element-1784" data-mp="25n081c5q82ov">
      <h3 className="text-lg font-bold text-dark-navy mb-6" data-id="element-1785" data-mp="2akii55vhcnyg">Item Activity</h3>

      <div className="relative border-l-2 border-slate-100 ml-3 space-y-8 pb-4" data-id="element-1786" data-mp="2ammjph5wpavk">
        <div className="relative pl-6" data-id="element-1787" data-mp="3ulq4d36my8g">
          <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-blue-100 border-2 border-white flex items-center justify-center" data-id="element-1788" data-mp="8t5cufploe1v">
            <div className="w-2 h-2 rounded-full bg-blue-500" data-id="element-1789" data-mp="ixvlt5jf8ifl" />
          </div>
          <div className="text-sm text-slate-500 mb-1" data-id="element-1790" data-mp="dysefefyy04g">Today, 2:45 PM</div>
          <div className="font-medium text-dark-navy" data-id="element-1791" data-mp="rpe0fc0pyq6k">Price updated</div>
          <div className="text-sm text-slate-600 mt-1" data-id="element-1792" data-mp="rridokkid1ji">
            Base price changed from AED 320 to AED 350.
          </div>
        </div>

        <div className="relative pl-6" data-id="element-1793" data-mp="11n26gy3rzk41">
          <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-purple-100 border-2 border-white flex items-center justify-center" data-id="element-1794" data-mp="11m0khyl81d7j">
            <div className="w-2 h-2 rounded-full bg-ai-purple" data-id="element-1795" data-mp="1ao032vbvwpcl" />
          </div>
          <div className="text-sm text-slate-500 mb-1" data-id="element-1796" data-mp="1alxcoa6tku7c">Yesterday, 11:20 AM</div>
          <div className="font-medium text-dark-navy" data-id="element-1797" data-mp="16ng3ll3u99na">AI Rule added</div>
          <div className="text-sm text-slate-600 mt-1" data-id="element-1798" data-mp="3cgobdj3dy2z8">
            Added rule: "Airport delivery AED 75".
          </div>
        </div>

        <div className="relative pl-6" data-id="element-1799" data-mp="38g4f0hqmz3e5">
          <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-green-100 border-2 border-white flex items-center justify-center" data-id="element-1800" data-mp="2hcrtn6jm1gms">
            <div className="w-2 h-2 rounded-full bg-green-500" data-id="element-1801" data-mp="2cdx8hxy67fzi" />
          </div>
          <div className="text-sm text-slate-500 mb-1" data-id="element-1802" data-mp="27l43d8ysi5mw">Oct 12, 09:00 AM</div>
          <div className="font-medium text-dark-navy" data-id="element-1803" data-mp="22o3xoz5ur4wb">Item published</div>
          <div className="text-sm text-slate-600 mt-1" data-id="element-1804" data-mp="1ylqqs7fdd0xy">
            Item moved from Draft to Active.
          </div>
        </div>

        <div className="relative pl-6" data-id="element-1805" data-mp="1tlmc8my0ugad">
          <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-slate-200 border-2 border-white flex items-center justify-center" data-id="element-1806" data-mp="1toqng49wmcaj">
            <div className="w-2 h-2 rounded-full bg-slate-400" data-id="element-1807" data-mp="1jozvoacqabe5" />
          </div>
          <div className="text-sm text-slate-500 mb-1" data-id="element-1808" data-mp="3j1bzosnmokq4">Oct 10, 04:15 PM</div>
          <div className="font-medium text-dark-navy" data-id="element-1809" data-mp="3izhmk0illwwl">Item created</div>
          <div className="text-sm text-slate-600 mt-1" data-id="element-1810" data-mp="3e30q8j18wc8f">
            Imported via Spreadsheet upload.
          </div>
        </div>
      </div>
    </div>;
}
function SettingsTab({
  onClose,
  bp,
  item
}: {
  onClose: () => void;
  bp: CategoryBlueprint;
  item: CatalogItem;
}) {
  const {
    trialActive,
    startTrial
  } = useBusiness();
  const VALIDITY_TOTAL = 30;
  const [published, setPublished] = useState(true);
  const [hideFromSearch, setHideFromSearch] = useState(false);
  const [archived, setArchived] = useState(false);
  const [daysRemaining, setDaysRemaining] = useState(12);
  const [showUnpublish, setShowUnpublish] = useState(false);
  const [unpublishReason, setUnpublishReason] = useState('sold');
  const [otherReason, setOtherReason] = useState('');
  const [showTrialPrompt, setShowTrialPrompt] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [itemCategory, setItemCategory] = useState(item.category);
  const [flash, setFlash] = useState<string | null>(null);
  const notify = (msg: string) => {
    setFlash(msg);
    window.setTimeout(() => setFlash(null), 2500);
  };
  const handlePublishToggle = () => {
    if (published) {
      // Reveal the unpublish reason flow rather than unpublishing instantly.
      setShowUnpublish(v => !v);
    } else {
      // Re-publishing requires an active trial.
      if (!trialActive) {
        setShowTrialPrompt(true);
      } else {
        setPublished(true);
        notify('Item published — now visible to customers.');
      }
    }
  };
  const confirmUnpublish = () => {
    setPublished(false);
    setShowUnpublish(false);
    notify('Item unpublished.');
  };
  const extendValidity = () => {
    setDaysRemaining(d => Math.min(VALIDITY_TOTAL, d + 30));
    notify('Validity extended by 30 days.');
  };
  const toggleHide = () => {
    setHideFromSearch(v => !v);
    notify(hideFromSearch ? 'Item is now searchable.' : 'Item hidden from search.');
  };
  const toggleArchive = () => {
    const next = !archived;
    setArchived(next);
    if (next) setPublished(false);
    notify(next ? 'Item archived.' : 'Item restored to active catalog.');
  };
  const validityPct = Math.round(daysRemaining / VALIDITY_TOTAL * 100);
  return <div className="max-w-2xl space-y-8" data-id="element-1811" data-mp="1h5v2e617x5es">
      {/* Flash feedback */}
      <AnimatePresence data-id="element-1812" data-mp="12gqj0liosv53">
        {flash && <motion.div initial={{
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} className="sticky top-0 z-20 flex items-center gap-2 px-4 py-2.5 bg-green-50 border border-green-200 text-green-800 text-sm font-medium rounded-xl shadow-sm" data-id="element-1813" data-mp="17dz0skzxax5t">
            <CheckCircle2Icon className="w-4 h-4 text-green-600" data-id="element-1814" data-mp="1q2wz7v29lh6e" />
            {flash}
          </motion.div>}
      </AnimatePresence>

      <section data-id="element-1815" data-mp="epbd6irpbvp3">
        <h3 className="text-lg font-bold text-dark-navy mb-4" data-id="element-1816" data-mp="eptg1y8vd85z">
          Classification
        </h3>
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-id="element-1817" data-mp="uwzbcy0o9obt">
          <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-1818" data-mp="q697jp7w9tj8">
            Category
          </label>
          <select value={itemCategory} onChange={e => {
          setItemCategory(e.target.value);
          notify(`Category set to ${e.target.value}.`);
        }} className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-indigo/20 text-sm outline-none" data-id="element-1819" data-mp="l6d3jpz7623o">
            {bp.categories.map(c => <option key={c} value={c} data-id="element-1820" data-mp="13wmcdiphos4c">
                {c}
              </option>)}
          </select>
          <p className="text-xs text-slate-500 mt-2" data-id="element-1821" data-mp="zv0vzndqxvpe">
            Categorizing your item helps customers find it faster when browsing
            or chatting with AI.
          </p>
        </div>
      </section>

      <section data-id="element-1822" data-mp="3kkgja8drb2aa">
        <h3 className="text-lg font-bold text-dark-navy mb-4" data-id="element-1823" data-mp="3ko4dl9r0zsq2">
          Listing Validity
        </h3>
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-id="element-1824" data-mp="201wa4s77v52w">
          <div className="flex items-center justify-between mb-2" data-id="element-1825" data-mp="2463wwju46ayh">
            <div className="font-medium text-dark-navy" data-id="element-1826" data-mp="294f74vobmnh8">Days Remaining</div>
            <div className="font-bold text-primary-indigo" data-id="element-1827" data-mp="2dv3z0c3wxmgw">
              {daysRemaining} days
            </div>
          </div>
          <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden mb-3" data-id="element-1828" data-mp="2iv9y2dciib90">
            <motion.div className="h-full bg-primary-indigo" animate={{
            width: `${validityPct}%`
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 26
          }} data-id="element-1829" data-mp="2nt1vtnzu8rl4" />
          </div>
          <p className="text-sm text-slate-500" data-id="element-1830" data-mp="xifg42nix12p">
            This listing is valid for {VALIDITY_TOTAL} days.
          </p>
          <button onClick={extendValidity} disabled={daysRemaining >= VALIDITY_TOTAL} className="mt-4 px-4 py-2 bg-slate-50 border border-slate-200 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1831" data-mp="12ggx8i1e2qbj">
            {daysRemaining >= VALIDITY_TOTAL ? 'Validity at maximum' : 'Extend Validity (+30 days)'}
          </button>
        </div>
      </section>

      <section data-id="element-1832" data-mp="2onvyat2hqi0w">
        <h3 className="text-lg font-bold text-dark-navy mb-4" data-id="element-1833" data-mp="2jqx8untruz91">
          Visibility &amp; Status
        </h3>
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-4" data-id="element-1834" data-mp="2xi2afvgpa1m0">
          <div className="flex items-center justify-between" data-id="element-1835" data-mp="2tgp4zmhis0fm">
            <div data-id="element-1836" data-mp="2th8mj239cmt4">
              <div className="font-medium text-dark-navy flex items-center gap-2" data-id="element-1837" data-mp="2tithgsthi7ri">
                Published Status
                <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded-md ${published ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-500'}`} data-id="element-1838" data-mp="32heygjjb5eq6">
                  {published ? 'Live' : 'Unpublished'}
                </span>
              </div>
              <div className="text-sm text-slate-500" data-id="element-1839" data-mp="3c59c1akapn88">
                Make this item visible to customers.
              </div>
            </div>
            <button onClick={handlePublishToggle} title="Toggle published" data-id="element-1840" data-mp="3h2iz4jk7pmsw">
              <ToggleRightIcon className={`w-10 h-10 transition-colors ${published ? 'text-green-500' : 'text-slate-300 rotate-180'}`} data-id="element-1841" data-mp="33bo0e03fgm31" />
            </button>
          </div>

          {showTrialPrompt && !trialActive && <div className="mt-4 p-4 bg-indigo-50 border border-indigo-100 rounded-xl" data-id="element-1842" data-mp="2tcopsbz3qgde">
              <div className="flex items-start gap-3" data-id="element-1843" data-mp="2ybi4u8u1effo">
                <SparklesIcon className="w-5 h-5 text-primary-indigo flex-shrink-0 mt-0.5" data-id="element-1844" data-mp="2kew7cyy32sls" />
                <div data-id="element-1845" data-mp="2ofoixyufh9l8">
                  <h4 className="font-bold text-indigo-900 mb-1" data-id="element-1846" data-mp="2oewxvhygiu97">
                    Activate your 7-day free trial
                  </h4>
                  <p className="text-sm text-indigo-800 mb-3" data-id="element-1847" data-mp="2zvuuyw61b671">
                    Start your free trial to publish inventory and start
                    receiving qualified leads.
                  </p>
                  <button onClick={() => {
                startTrial();
                setShowTrialPrompt(false);
                setPublished(true);
                notify('Trial started — item published.');
              }} className="px-4 py-2 bg-primary-indigo text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors" data-id="element-1848" data-mp="2pxotgfo5422p">
                    Start Free Trial
                  </button>
                </div>
              </div>
            </div>}

          <AnimatePresence initial={false} data-id="element-1849" data-mp="227axwf6cjl3d">
            {showUnpublish && published && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} className="overflow-hidden" data-id="element-1850" data-mp="1y70r50aqcqng">
                <div className="pl-4 border-l-2 border-slate-100 space-y-3" data-id="element-1851" data-mp="rng8kxb9jiev">
                  <label className="block text-sm font-medium text-slate-700" data-id="element-1852" data-mp="3q76c1b97fau7">
                    Reason for unpublishing
                  </label>
                  <select value={unpublishReason} onChange={e => setUnpublishReason(e.target.value)} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-indigo/20 text-sm" data-id="element-1853" data-mp="3yn8conlyoeg">
                    <option value="sold" data-id="element-1854" data-mp="3ce6y3dp2loor">Sold</option>
                    <option value="not_for_sale" data-id="element-1855" data-mp="3ccvrz785k4uh">Not for sale anymore</option>
                    <option value="other" data-id="element-1856" data-mp="3h9kcx6cbpds1">Other</option>
                  </select>
                  {unpublishReason === 'other' && <input type="text" value={otherReason} onChange={e => setOtherReason(e.target.value)} placeholder="Please specify..." className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-indigo/20 text-sm" data-id="element-1857" data-mp="3m9oku7z6106n" />}
                  <div className="flex gap-2" data-id="element-1858" data-mp="drsdx7zrrtkc">
                    <button onClick={confirmUnpublish} className="px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors" data-id="element-1859" data-mp="dt3hp7t7t1bg">
                      Confirm Unpublish
                    </button>
                    <button onClick={() => setShowUnpublish(false)} className="px-4 py-2 text-slate-600 text-sm font-medium rounded-lg hover:bg-slate-100 transition-colors" data-id="element-1860" data-mp="rscz2at5q1h0">
                      Cancel
                    </button>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>

          <div className="h-px bg-slate-100" data-id="element-1861" data-mp="1ybe2f4ucyzf9" />
          <div className="flex items-center justify-between" data-id="element-1862" data-mp="2vxbqdhribwyl">
            <div data-id="element-1863" data-mp="2vws45outqmer">
              <div className="font-medium text-dark-navy" data-id="element-1864" data-mp="2vzechqpjh5y1">Hide from Search</div>
              <div className="text-sm text-slate-500" data-id="element-1865" data-mp="2qza3smuvhfid">
                Item can only be accessed via direct link.
              </div>
            </div>
            <button onClick={toggleHide} title="Toggle hide from search" data-id="element-1866" data-mp="2h9gvpvtxviiv">
              <ToggleRightIcon className={`w-10 h-10 transition-colors ${hideFromSearch ? 'text-primary-indigo' : 'text-slate-300 rotate-180'}`} data-id="element-1867" data-mp="3eo2v260juz6n" />
            </button>
          </div>
        </div>
      </section>

      <section data-id="element-1868" data-mp="180vjj2c30bym">
        <h3 className="text-lg font-bold text-dark-navy mb-4" data-id="element-1869" data-mp="180c906orgpum">Danger Zone</h3>
        <div className="bg-red-50 border border-red-100 rounded-xl p-4 space-y-4" data-id="element-1870" data-mp="1c0co7tjop52q">
          <div className="flex items-center justify-between" data-id="element-1871" data-mp="yasq5111svzr">
            <div data-id="element-1872" data-mp="y86hsz6c2cgh">
              <div className="font-medium text-red-900" data-id="element-1873" data-mp="138u9zo6aszca">
                {archived ? 'Restore Item' : 'Archive Item'}
              </div>
              <div className="text-sm text-red-700" data-id="element-1874" data-mp="ob1uux4nycym">
                {archived ? 'Bring this item back into your active catalog.' : 'Remove from active catalog but keep data.'}
              </div>
            </div>
            <button onClick={toggleArchive} className="px-4 py-2 bg-white border border-red-200 text-red-700 font-medium rounded-lg hover:bg-red-100 flex items-center gap-2 transition-colors" data-id="element-1875" data-mp="ka88472yj47b">
              <ArchiveIcon className="w-4 h-4" data-id="element-1876" data-mp="2en7sedia8mkx" />{' '}
              {archived ? 'Restore' : 'Archive'}
            </button>
          </div>
          <div className="h-px bg-red-100" data-id="element-1877" data-mp="2osfuvs7dsr2v" />
          <div className="flex items-center justify-between" data-id="element-1878" data-mp="2jtmnmhiotwb5">
            <div data-id="element-1879" data-mp="2jr091xxdqgjf">
              <div className="font-medium text-red-900" data-id="element-1880" data-mp="2xkhrtgi8rp8e">Delete Item</div>
              <div className="text-sm text-red-700" data-id="element-1881" data-mp="2xmm1yg6qhkh4">
                Permanently remove this item and all its data.
              </div>
            </div>
            {confirmDelete ? <div className="flex items-center gap-2" data-id="element-1882" data-mp="37guh826r7gxc">
                <button onClick={() => setConfirmDelete(false)} className="px-3 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors" data-id="element-1883" data-mp="32k45fr1zf7hq">
                  Cancel
                </button>
                <button onClick={onClose} className="px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 flex items-center gap-2 transition-colors" data-id="element-1884" data-mp="3cfxo7s6anx7y">
                  <TrashIcon className="w-4 h-4" data-id="element-1885" data-mp="1jl6exn2wl3zs" /> Confirm Delete
                </button>
              </div> : <button onClick={() => setConfirmDelete(true)} className="px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 flex items-center gap-2 transition-colors" data-id="element-1886" data-mp="1oiy9kouxn47o">
                <TrashIcon className="w-4 h-4" data-id="element-1887" data-mp="1xilal9jr2807" /> Delete
              </button>}
          </div>
        </div>
      </section>
    </div>;
}
interface KnowledgeRule {
  id: number;
  type: string;
  cond: string;
  charge: string;
  active: boolean;
}
interface KnowledgeFaq {
  id: number;
  q: string;
  a: string;
  approved: boolean;
}
function AIRulesTab({
  bp,
  item,
  categories,
  onOpenSellingRules
}: {
  bp: CategoryBlueprint;
  item: CatalogItem;
  categories: string[];
  onOpenSellingRules?: (category?: string) => void;
}) {
  const itemTitle = item.title;
  const [rules, setRules] = useState<KnowledgeRule[]>([{
    id: 1,
    type: 'Delivery Rule',
    cond: 'Dubai delivery',
    charge: 'AED 50',
    active: true
  }, {
    id: 2,
    type: 'Pickup Rule',
    cond: 'Dubai pickup',
    charge: 'AED 50',
    active: true
  }, {
    id: 3,
    type: 'Airport Rule',
    cond: 'Airport delivery',
    charge: 'AED 75',
    active: true
  }, {
    id: 4,
    type: 'Deposit Rule',
    cond: 'Security deposit',
    charge: 'AED 1,000',
    active: true
  }, {
    id: 5,
    type: 'Restriction',
    cond: 'Minimum rental period',
    charge: '2 days',
    active: false
  }]);
  const [draft, setDraft] = useState('');
  // What's worth teaching Talab depends entirely on what the item is.
  const suggestions = bp.knowledge;
  // Parse a natural-language rule into a structured row. Pulls out any
  // "AED <amount>" or "<n> day(s)" as the charge, classifies the type by
  // keyword, and uses the full text as the condition.
  const buildRule = (text: string): KnowledgeRule => {
    const trimmed = text.trim();
    const chargeMatch = trimmed.match(/AED\s?[\d,]+/i);
    const durationMatch = trimmed.match(/\d+\s?days?/i);
    const charge = chargeMatch ? chargeMatch[0].toUpperCase().replace('AED', 'AED ').replace(/\s+/g, ' ').trim() : durationMatch ? durationMatch[0] : '—';
    const lower = trimmed.toLowerCase();
    let type = 'Custom Rule';
    if (lower.includes('deliver')) type = 'Delivery Rule';else if (lower.includes('pickup') || lower.includes('pick up')) type = 'Pickup Rule';else if (lower.includes('airport')) type = 'Airport Rule';else if (lower.includes('deposit')) type = 'Deposit Rule';else if (lower.includes('no ') || lower.includes('not ') || lower.includes('minimum') || lower.includes('restrict')) type = 'Restriction';
    return {
      id: Date.now(),
      type,
      cond: trimmed,
      charge,
      active: true
    };
  };
  const addRule = (text?: string) => {
    const value = (text ?? draft).trim();
    if (!value) return;
    setRules(prev => [buildRule(value), ...prev]);
    if (!text) setDraft('');
  };
  const toggleRule = (id: number) => setRules(prev => prev.map(r => r.id === id ? {
    ...r,
    active: !r.active
  } : r));
  const deleteRule = (id: number) => setRules(prev => prev.filter(r => r.id !== id));
  const activeCount = rules.filter(r => r.active).length;
  // ---- FAQs ----
  const [faqs, setFaqs] = useState<KnowledgeFaq[]>([{
    id: 101,
    q: 'What is the minimum rental period?',
    a: 'The minimum rental period for this vehicle is 2 days.',
    approved: true
  }, {
    id: 102,
    q: 'Do you offer delivery to the airport?',
    a: 'Yes, airport delivery is available for an additional charge of AED 75.',
    approved: true
  }, {
    id: 103,
    q: 'Is insurance included in the price?',
    a: 'Basic comprehensive insurance is included. Full coverage is available as an add-on.',
    approved: false
  }, {
    id: 104,
    q: 'What documents are required to rent?',
    a: 'You will need your Emirates ID (or Passport with Visa) and a credit card for the security deposit.',
    approved: true
  }]);
  const [adding, setAdding] = useState(false);
  const [draftQ, setDraftQ] = useState('');
  const [draftA, setDraftA] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editQ, setEditQ] = useState('');
  const [editA, setEditA] = useState('');
  const addFaq = () => {
    if (!draftQ.trim() || !draftA.trim()) return;
    setFaqs(prev => [{
      id: Date.now(),
      q: draftQ.trim(),
      a: draftA.trim(),
      approved: true
    }, ...prev]);
    setDraftQ('');
    setDraftA('');
    setAdding(false);
  };
  const deleteFaq = (id: number) => setFaqs(prev => prev.filter(f => f.id !== id));
  const toggleApprove = (id: number) => setFaqs(prev => prev.map(f => f.id === id ? {
    ...f,
    approved: !f.approved
  } : f));
  const startEdit = (f: KnowledgeFaq) => {
    setEditingId(f.id);
    setEditQ(f.q);
    setEditA(f.a);
  };
  const saveEdit = () => {
    setFaqs(prev => prev.map(f => f.id === editingId ? {
      ...f,
      q: editQ.trim(),
      a: editA.trim()
    } : f));
    setEditingId(null);
  };
  const approvedCount = faqs.filter(f => f.approved).length;
  return <div className="flex flex-col h-full max-w-4xl" data-id="element-1888" data-mp="25ucyjkfsibop">
      <div className="mb-5" data-id="element-1889" data-mp="zd7yavoyeshb">
        <h3 className="text-lg font-bold text-dark-navy mb-2" data-id="element-1890" data-mp="zdhq8otk79pn">AI Knowledge</h3>
        <p className="text-sm text-slate-500" data-id="element-1891" data-mp="ucaiuqvbwcji">
          What Talab should <span className="font-semibold" data-id="element-1892" data-mp="3nsu4f7zajrk1">know</span> about
          this item — and what it may <span className="font-semibold" data-id="element-1893" data-mp="3jrqludpuzhf4">
            suggest
          </span>{' '}
          alongside it.
        </p>
      </div>

      <div className="flex-1 overflow-y-auto -mr-2 pr-2 space-y-10" data-id="element-1894" data-mp="1esv8xeyi0xm">
        {/* ============ SELLING RECOMMENDATIONS ============
            A read-out by default — this is configured per category, not per
            item, so most merchants never touch it here. */}
        <SellingRecommendations itemId={item.id} itemTitle={itemTitle} category={item.category} kind={bp.id} categories={categories} onOpenCategoryRules={c => onOpenSellingRules?.(c)} data-id="element-1895" data-mp="6d44p71ca9fb" />

        {/* ============ SECTION 1: PRICING & RULES ============ */}
        <section data-id="element-1896" data-mp="2m36wyk0br2mj">
          <div className="flex items-center justify-between mb-4" data-id="element-1897" data-mp="2m4rrwaqjwnkx">
            <div className="flex items-center gap-2.5" data-id="element-1898" data-mp="2h5f5ts7i7kje">
              <div className="w-8 h-8 rounded-lg bg-ai-purple/10 flex items-center justify-center" data-id="element-1899" data-mp="2h6zzzgpwp1gq">
                <SparklesIcon className="w-4 h-4 text-ai-purple" data-id="element-1900" data-mp="woxpt4xexwky" />
              </div>
              <div data-id="element-1901" data-mp="11l61g8cej7nd">
                <h4 className="font-bold text-dark-navy leading-tight" data-id="element-1902" data-mp="11ns907zaln5l">
                  Pricing &amp; Rules
                </h4>
                <p className="text-xs text-slate-500" data-id="element-1903" data-mp="rvmlpbjzjtwq">
                  {activeCount} of {rules.length} rules active
                </p>
              </div>
            </div>
          </div>

          {/* Natural Language Input */}
          <div className="mb-4" data-id="element-1904" data-mp="ix13ximc8ix0">
            <div className="relative" data-id="element-1905" data-mp="it3k0urxrnc2">
              <div className="absolute top-3 left-3 text-ai-purple" data-id="element-1906" data-mp="4wgmoqw2mv5m">
                <SparklesIcon className="w-5 h-5" data-id="element-1907" data-mp="8xa5j5unpjrn" />
              </div>
              <textarea rows={2} value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={e => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                addRule();
              }
            }} placeholder='E.g. "If customer wants delivery to Sharjah, add AED 100."' className="w-full pl-10 pr-28 py-3 bg-purple-50/50 border border-purple-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-ai-purple/30 focus:border-ai-purple text-sm resize-none" data-id="element-1908" data-mp="8zcwbs3mvgxf" />
              <button onClick={() => addRule()} disabled={!draft.trim()} className="absolute right-2 bottom-2 px-3 py-1.5 bg-ai-purple text-white text-xs font-bold rounded-lg hover:bg-purple-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1" data-id="element-1909" data-mp="3uxob44l4u75f">
                <PlusIcon className="w-3.5 h-3.5" data-id="element-1910" data-mp="3h5i6nfrcdolt" /> Add Rule
              </button>
            </div>
            <div className="flex flex-wrap gap-2 mt-3" data-id="element-1911" data-mp="ij5n2dsg28cn">
              {suggestions.map(s => <button key={s} onClick={() => addRule(s)} className="px-3 py-1.5 bg-white border border-slate-200 text-slate-600 text-xs font-medium rounded-full hover:border-ai-purple hover:text-ai-purple hover:bg-purple-50/50 transition-colors flex items-center gap-1" data-id="element-1912" data-mp="dorjho3mouww">
                  <PlusIcon className="w-3 h-3" data-id="element-1913" data-mp="z2l43c9ywcsy" /> {s}
                </button>)}
            </div>
          </div>

          {/* Structured Rules Table */}
          <div className="border border-slate-200 rounded-xl overflow-hidden" data-id="element-1914" data-mp="18zg1tsyf2966">
            <table className="w-full text-left text-sm" data-id="element-1915" data-mp="18xd22h7gp1o9">
              <thead className="bg-slate-50 border-b border-slate-200" data-id="element-1916" data-mp="1drr1cup8vsy7">
                <tr data-id="element-1917" data-mp="1iq0vr335apir">
                  <th className="px-4 py-3 font-medium text-slate-600" data-id="element-1918" data-mp="1iohgnft86kik">
                    Rule Type
                  </th>
                  <th className="px-4 py-3 font-medium text-slate-600" data-id="element-1919" data-mp="1msxlrev0lnwq">
                    Condition
                  </th>
                  <th className="px-4 py-3 font-medium text-slate-600" data-id="element-1920" data-mp="1rnuv6mw7hza5">
                    Charge
                  </th>
                  <th className="px-4 py-3 font-medium text-slate-600" data-id="element-1921" data-mp="1a2et37npl03c">
                    Status
                  </th>
                  <th className="px-4 py-3 font-medium text-slate-600 text-right" data-id="element-1922" data-mp="1ns8jhuev3sf6">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-1923" data-mp="v42wf10c5rc6">
                <AnimatePresence initial={false} data-id="element-1924" data-mp="v6p3z0n886ue">
                  {rules.map(rule => <motion.tr key={rule.id} layout initial={{
                  opacity: 0,
                  backgroundColor: 'rgba(168,85,247,0.06)'
                }} animate={{
                  opacity: 1,
                  backgroundColor: 'rgba(255,255,255,0)'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} className={`hover:bg-slate-50 transition-colors group ${rule.active ? '' : 'opacity-60'}`} data-id="element-1925" data-mp="r4u6y8cbwfmf">
                      <td className="px-4 py-3 font-medium text-dark-navy whitespace-nowrap" data-id="element-1926" data-mp="3kh49yufj561q">
                        {rule.type}
                      </td>
                      <td className="px-4 py-3 text-slate-600 max-w-xs" data-id="element-1927" data-mp="gzcdp3ud1zxq">
                        {rule.cond}
                      </td>
                      <td className="px-4 py-3 text-slate-900 font-medium whitespace-nowrap" data-id="element-1928" data-mp="kze4ghyk58y8">
                        {rule.charge}
                      </td>
                      <td className="px-4 py-3" data-id="element-1929" data-mp="c843i5l7iaji">
                        <button onClick={() => toggleRule(rule.id)} title="Toggle status" className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md transition-colors ${rule.active ? 'bg-green-50 text-green-700 hover:bg-green-100' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`} data-id="element-1930" data-mp="c6szq5rrh2se">
                          {rule.active ? <>
                              <CheckCircle2Icon className="w-3 h-3" data-id="element-1931" data-mp="14l67fzt55kah" /> Active
                            </> : <>
                              <ClockIcon className="w-3 h-3" data-id="element-1932" data-mp="vmazai6je04j" /> Paused
                            </>}
                        </button>
                      </td>
                      <td className="px-4 py-3" data-id="element-1933" data-mp="lvq5d81t5jry">
                        <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1934" data-mp="lwhp9lm1lt2e">
                          <button onClick={() => toggleRule(rule.id)} title={rule.active ? 'Pause rule' : 'Activate rule'} className="p-1.5 text-slate-400 hover:text-primary-indigo hover:bg-indigo-50 rounded-md transition-colors" data-id="element-1935" data-mp="gv28i5qwe8pt">
                            <ToggleRightIcon className="w-4 h-4" data-id="element-1936" data-mp="2nk46is6acw41" />
                          </button>
                          <button onClick={() => deleteRule(rule.id)} title="Delete rule" className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors" data-id="element-1937" data-mp="2sla1kwnf4vgq">
                            <TrashIcon className="w-4 h-4" data-id="element-1938" data-mp="24vpdy0gbla14" />
                          </button>
                        </div>
                      </td>
                    </motion.tr>)}
                </AnimatePresence>
                {rules.length === 0 && <tr data-id="element-1939" data-mp="1h5lc9ymozkcr">
                    <td colSpan={5} className="px-4 py-12 text-center text-sm text-slate-400" data-id="element-1940" data-mp="1h41shy1tur68">
                      No rules yet. Add one above to teach the AI how to handle
                      this item.
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>
        </section>

        {/* ============ SECTION 2: FAQs ============ */}
        <section data-id="element-1941" data-mp="jip7gso3f2l2">
          <div className="flex items-center justify-between mb-4" data-id="element-1942" data-mp="ji5za3o8vsk8">
            <div className="flex items-center gap-2.5" data-id="element-1943" data-mp="yfq1j2trb9mp">
              <div className="w-8 h-8 rounded-lg bg-primary-indigo/10 flex items-center justify-center" data-id="element-1944" data-mp="ye58xir060rh">
                <MessageSquareIcon className="w-4 h-4 text-primary-indigo" data-id="element-1945" data-mp="tgd3rn1cki5c" />
              </div>
              <div data-id="element-1946" data-mp="177i5cuo9zkib">
                <h4 className="font-bold text-dark-navy leading-tight" data-id="element-1947" data-mp="179unp8xy3wvw">
                  Frequently Asked Questions
                </h4>
                <p className="text-xs text-slate-500" data-id="element-1948" data-mp="1h5o5p7ufoil2">
                  {approvedCount} of {faqs.length} approved for AI replies
                </p>
              </div>
            </div>
            <button onClick={() => setAdding(v => !v)} className="px-3.5 py-2 bg-primary-indigo/10 text-primary-indigo text-sm font-semibold rounded-lg hover:bg-primary-indigo/20 transition-colors flex items-center gap-1.5" data-id="element-1949" data-mp="1vt679xcp8x39">
              <PlusIcon className="w-4 h-4" data-id="element-1950" data-mp="1lwtc0v1iab6e" /> Add FAQ
            </button>
          </div>

          {/* Add FAQ form */}
          <AnimatePresence initial={false} data-id="element-1951" data-mp="1d2ewqsgz70na">
            {adding && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} className="overflow-hidden" data-id="element-1952" data-mp="1d0c03plbi99l">
                <div className="p-4 mb-4 bg-slate-50 border border-slate-200 rounded-xl space-y-3" data-id="element-1953" data-mp="u5ni4wcdp9pk">
                  <input type="text" value={draftQ} onChange={e => setDraftQ(e.target.value)} placeholder="Question — e.g. Can I extend my rental?" className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo transition-all" data-id="element-1954" data-mp="y78vsjwqls0t" />
                  <textarea rows={2} value={draftA} onChange={e => setDraftA(e.target.value)} placeholder="Answer the AI should give…" className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo transition-all" data-id="element-1955" data-mp="3nslcw4r2qknj" />
                  <div className="flex justify-end gap-2" data-id="element-1956" data-mp="5o07xrqw570b">
                    <button onClick={() => {
                  setAdding(false);
                  setDraftQ('');
                  setDraftA('');
                }} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 rounded-lg transition-colors" data-id="element-1957" data-mp="5mfey6k85w4k">
                      Cancel
                    </button>
                    <button onClick={addFaq} disabled={!draftQ.trim() || !draftA.trim()} className="px-4 py-2 bg-primary-indigo text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-1958" data-mp="rucdnobzh8b4">
                      Save FAQ
                    </button>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>

          {/* FAQ list */}
          <div className="space-y-3" data-id="element-1959" data-mp="1jn3b225el31y">
            <AnimatePresence initial={false} data-id="element-1960" data-mp="1jo4t4qku6pt6">
              {faqs.map(faq => <motion.div key={faq.id} layout initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              height: 0
            }} className="p-4 border border-slate-200 rounded-xl hover:border-slate-300 hover:shadow-sm transition-all group" data-id="element-1961" data-mp="3gp0vp8y0s4x6">
                  {editingId === faq.id ? <div className="space-y-3" data-id="element-1962" data-mp="c8pv1bgnkec3">
                      <input type="text" value={editQ} onChange={e => setEditQ(e.target.value)} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo" data-id="element-1963" data-mp="88fq824ljtyt" />
                      <textarea rows={2} value={editA} onChange={e => setEditA(e.target.value)} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo" data-id="element-1964" data-mp="23x7ij7ukt87h" />
                      <div className="flex justify-end gap-2" data-id="element-1965" data-mp="1l6pu3fgs0yrx">
                        <button onClick={() => setEditingId(null)} className="px-3 py-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 rounded-lg transition-colors" data-id="element-1966" data-mp="1l4v43mqneggz">
                          Cancel
                        </button>
                        <button onClick={saveEdit} className="px-3 py-1.5 bg-primary-indigo text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-1.5" data-id="element-1967" data-mp="36kamqj4780jg">
                          <CheckIcon className="w-3.5 h-3.5" data-id="element-1968" data-mp="11gvhapw7xwna" /> Save
                        </button>
                      </div>
                    </div> : <div className="flex items-start justify-between gap-4" data-id="element-1969" data-mp="shqlhrc1khet">
                      <div className="flex-1 min-w-0" data-id="element-1970" data-mp="nk8lwrjwzejh">
                        <div className="flex items-center gap-2 mb-2" data-id="element-1971" data-mp="nl08xdzg43y5">
                          <h4 className="font-bold text-dark-navy flex items-center gap-2" data-id="element-1972" data-mp="ir5pmbnr5qzl">
                            <span className="text-slate-400" data-id="element-1973" data-mp="dtx3y13eb4tl">Q:</span> {faq.q}
                          </h4>
                          {!faq.approved && <span className="px-2 py-0.5 bg-amber-50 text-amber-600 text-[10px] font-bold uppercase tracking-wide rounded-md" data-id="element-1974" data-mp="4t7cf9l640lo">
                              Draft
                            </span>}
                        </div>
                        <p className="text-sm text-slate-600 flex items-start gap-2" data-id="element-1975" data-mp="2bjao888yholb">
                          <span className="text-ai-purple font-bold mt-0.5" data-id="element-1976" data-mp="2gi5hy58goxk7">
                            A:
                          </span>{' '}
                          {faq.a}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0" data-id="element-1977" data-mp="1sqqci3le204q">
                        <button onClick={() => toggleApprove(faq.id)} title={faq.approved ? 'Approved — click to unapprove' : 'Approve for AI'} className={`p-1.5 rounded-md transition-colors ${faq.approved ? 'text-green-600 bg-green-50 hover:bg-green-100' : 'text-slate-400 hover:text-green-600 hover:bg-green-50'}`} data-id="element-1978" data-mp="1sonccqqiuqma">
                          <CheckCircle2Icon className="w-4 h-4" data-id="element-1979" data-mp="2hboep4y3z668" />
                        </button>
                        <button onClick={() => startEdit(faq)} className="p-1.5 text-slate-400 hover:text-primary-indigo hover:bg-indigo-50 rounded-md transition-colors" title="Edit" data-id="element-1980" data-mp="2ce6hgbtgefe2">
                          <EditIcon className="w-4 h-4" data-id="element-1981" data-mp="1yd3rhnzc8kxv" />
                        </button>
                        <button onClick={() => deleteFaq(faq.id)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors" title="Delete" data-id="element-1982" data-mp="1tesh9c54s8f4">
                          <TrashIcon className="w-4 h-4" data-id="element-1983" data-mp="22hjw7lx7tmbi" />
                        </button>
                      </div>
                    </div>}
                </motion.div>)}
            </AnimatePresence>
            {faqs.length === 0 && <div className="px-4 py-10 text-center text-sm text-slate-400 border border-dashed border-slate-200 rounded-xl" data-id="element-1984" data-mp="3ma37aumfrjzq">
                No FAQs yet. Add one so the AI can answer common questions
                instantly.
              </div>}
          </div>
        </section>
      </div>
    </div>;
}